import {
  bodyComposition, BodyMetricGraphTypes, BodyScanTypes, IBodyAssessment,
  IBodyFatAdjustments, IBodyMetricModel, IBodyScanData, IBodyScanModel, IBodyScansAndLatest, IBodyScanStat,
  IMinMaxDefault, IScanStats, IStat, ITargetInfo, ITargetInfoViewModel, ITargetMetricValues, IWeeklyProgress,
  IWeeklyWorkoutMinutesTarget, IWeightTargetInfo,
  IWorkoutMinutes, IWorkoutStats, MassUnit, MetricToImperialConverter, NutritionRating,
  ProgramType, ScanStat, StatMonth, TimeSpan, UserTargetTypes, WorkoutStat,
} from 'fitbeat.models';
import moment from 'moment';
import { StatRangesConfig } from '../../config';
import { BigBodyGroup, UserProfile } from '../../entities';
import {
  IBodyScansRepository, IUserProfileRepository,
  IUserWorkoutStatsRepository, IWorkoutRepository,
} from '../../persistence';
import { BodyFatAdjustmentCalculations } from './bodyFatAdjustmentCalculator';
import {
  bodyscansErrorHandler, calcChangeDiff, calcMuscleMassMeter, calcMuscleMassMeterKg,
  calcTotalMinutes, calculatebodyFatMassFromBodyFatPercentage, calculatebodyFatPercentageFromBodyFatMass,
  changeDescription, getAnalysisValue, getDaysInMonth, getMinMaxGraphDataPoints,
  getMuscleMassMeterGraphDataPoints, getNutritionRatingsGraphDataPoints, getSomatotypeBasedOnAnswers,
  getTotalMinutesCeil, getUserScanStatsGraphDataPoints, getWorkoutStatsGraphDataPoints, groupBy,
  groupedSequenceFromRatingsForRequestedMonth, heartRateMonitorErrorHandler,
  nutritionRatingsErrorHandler, round, scanStatsErrorHandler, takeFirstOnGroup,
} from './StatHelpers';
import {
  calcMinMaxDefaulTarget, calcTargetDateBasedOnModel,
  calcWeightTargetDate, ensureInputIsInMetricUnits,
} from './TargetUtils';
import { workoutMinutesTargetConfig, WorkoutTargetConfig } from './WorkoutTargetConfig';
const nutitionRatingTransformDenominator = 20;
const graphWeeks = [1, 2, 3, 4, 5, 6];
const maxHistoricMonths = 8;

export class UserTrackingStats {
  constructor(private userWorkoutStatsRepository: IUserWorkoutStatsRepository,
              private userProfileRepository: IUserProfileRepository,
              private bodyScanRepository: IBodyScansRepository,
              private workoutRepository: IWorkoutRepository,
              private logger: any,
  ) { }

  public async getBodyMetricSet(uid: string, requestTime: Date, timezone: string)
    : Promise<(IBodyMetricModel | null)[] | null> {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw new Error(`Failed to load user with id: ${uid}`);
    }

    const bodyScans = await this.getBodyScans(user, requestTime, timezone);

    const historyFromDate =
      moment(requestTime).tz(timezone).clone()
        .subtract(maxHistoricMonths, 'months').endOf('day');

    return this.getBodyMetricSetForUser(user, bodyScans, historyFromDate);
  }

  public async getBodyweightMetricSet(uid: string, requestTime: Date, timezone: string) {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw new Error(`Failed to load user with id: ${uid}`);
    }

    const bodyScans = await this.getBodyScans(user, requestTime, timezone);
    return await this.getBodyMetricTargetAndHistory(UserTargetTypes.Bodyweight, bodyScans, user);
  }

  public async getWeightTarget(user: UserProfile) {
    const latestBodyscan = await this.bodyScanRepository.getLastBodyScan(user.uid);
    if (!latestBodyscan) {
      return {
        target: undefined,
        milestone: undefined,
      } as IWeightTargetInfo;
    }
    const mappedScan = this.mapScansToBodyScanModel(latestBodyscan);
    const weightTargetInfo = this.weightTargetInfoFromScan(user, mappedScan);
    const userTargetType = this.getDefaultUserTargetType(user);
    return this.convertTargetInfoToImperialIfRequired(weightTargetInfo, user, userTargetType);
  }

  public async getWeightTargetAndBodyMetricSet(
    uid: string, requestTime: Date, timezone: string) {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw new Error(`Failed to load user with id: ${uid}`);
    }

    const bodyScans = await this.getBodyScans(user, requestTime, timezone);

    const weightTargetInfo: IWeightTargetInfo = {
      ...this.weightTargetInfoFromScan(user, bodyScans ? bodyScans.latest : undefined),
    };

    let userTargetType = this.getTargetTypeFromTarget(bodyScans.latest);

    if (!userTargetType) {
      userTargetType = this.getDefaultUserTargetType(user);
    }

    const historyFromDate =
      moment(requestTime).tz(timezone).clone()
        .subtract(maxHistoricMonths, 'months').endOf('day');
    const bodyMetricSet = this.getBodyMetricSetForUser(user, bodyScans, historyFromDate);

    return {
      weightTargetInfo: this.convertTargetInfoToImperialIfRequired(weightTargetInfo, user, userTargetType),
      bodyMetricSet,
    };
  }

  public async calculateMilestoneAndUpdateLatestScan(uid: string, targetInfo: any, userTargetType: UserTargetTypes) {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw new Error(`Failed to load user with id: ${uid}`);
    }
    const latestBodyscan = await this.bodyScanRepository.getLastBodyScan(uid);
    if (!latestBodyscan) {
      return;
    }
    const latestScanDate = new Date(latestBodyscan.dateOfScan);
    const milestoneInfo = this.calculateMilestoneFromTarget(latestScanDate, targetInfo, user.weight, userTargetType);

    return await this.bodyScanRepository.updateScanWithTargetAndMilestone(latestBodyscan.id, targetInfo, milestoneInfo);
  }

  public async updateUserTargetAndMilestone(
    uid: string,
    newWeight: number,
    PALValue: number,
    requestTime: Date,
    timezone: string,
    userTargetType: UserTargetTypes,
    rawScan: any | undefined,
    targetMetricValues?: ITargetMetricValues,
  ) {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw new Error(`Failed to load user with id: ${uid}`);
    }

    let target: ITargetInfo | undefined;
    let milestone: ITargetInfo | undefined;

    const bodyScans = await this.getBodyScans(user, requestTime, timezone);
    let updatedBodyScans: IBodyScansAndLatest = bodyScans;

    if (userTargetType === UserTargetTypes.Bodyweight) {
      this.updateUserWeightSynchronous(user, newWeight, PALValue);

      if (bodyScans.latest && bodyScans.latest.target && !bodyScans.latest.target.reached) {
        const targetAndMilestoneInfo = this.recalculateWeightTargetDate(
          user,
          bodyScans.latest,
          requestTime,
          timezone,
          newWeight,
        );
        target = targetAndMilestoneInfo.target;
        milestone = targetAndMilestoneInfo.milestone;
      }
    }

    if (userTargetType === UserTargetTypes.Musclemass) {
      if (bodyScans.latest && bodyScans.latest.target && !bodyScans.latest.target.reached) {
        const targetAndMilestoneInfo = this.recalculateMuscleMassTargetDate(
          user,
          bodyScans.latest,
          requestTime,
          timezone,
          targetMetricValues);
        target = targetAndMilestoneInfo.target;
        milestone = targetAndMilestoneInfo.milestone;
      }
    }

    if (userTargetType === UserTargetTypes.Bodyfat) {
      if (bodyScans.latest && bodyScans.latest.target && !bodyScans.latest.target.reached) {
        const targetAndMilestoneInfo = this.recalculateBodyFatTargetDate(
          user,
          bodyScans.latest,
          requestTime,
          timezone,
          targetMetricValues);
        target = targetAndMilestoneInfo.target;
        milestone = targetAndMilestoneInfo.milestone;
      }
    }

    let mappedStats;
    const logDate = moment(requestTime).tz(timezone);
    if (rawScan) {
      const scanedEntry =
        await this.addBodyScaleScanForUser(user, logDate.format('YYYY-MM-DD HH:mm:ss'), rawScan, target, milestone);
      mappedStats = this.mapStats(scanedEntry);
    } else {
      const manaulEntry =
        await this.logManualWeight(uid, logDate.format('YYYY-MM-DD HH:mm:ss'), newWeight, target, milestone);
      mappedStats = this.mapStats(manaulEntry);
    }

    const mappedScan = this.mapScans(user, mappedStats);
    updatedBodyScans = {
      scans: [
        ...bodyScans.scans,
        mappedScan,
      ],
      latest: mappedScan,
    };

    const historyFromDate =
      moment(requestTime).tz(timezone).clone()
        .subtract(maxHistoricMonths, 'months').endOf('day');

    let weightTargetInfo: IWeightTargetInfo = {
      target,
      milestone,
    };

    const hasReachedTarget = (weightTargetInfo.target && weightTargetInfo.target.reached) ? true : false;

    const bodyMetricSet = this.getBodyMetricSetForUser(
      user,
      updatedBodyScans,
      historyFromDate,
      hasReachedTarget,
    );

    weightTargetInfo = this.convertTargetInfoToImperialIfRequired(weightTargetInfo, user, userTargetType);

    return {
      weightTargetInfo,
      bodyMetricSet,
    };
  }

  public async getMinMaxDefaulTarget(
    uid: string,
    requestTime: Date,
    timezone: string,
    userTargetType: UserTargetTypes,
  ) {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw new Error(`Failed to load user with id: ${uid}`);
    }

    const lastBodyScan = await this.bodyScanRepository.getLastBodyScan(uid);
    if (!lastBodyScan) {
      throw new Error(`Cannot calculate min max default wihout a scan`);
    }

    const result = calcMinMaxDefaulTarget(user, requestTime, timezone, userTargetType, lastBodyScan);
    const isBodyFatTarget = userTargetType === UserTargetTypes.Bodyfat;
    if (user.useImperialUnit && !isBodyFatTarget) {
      return this.convertMinMaxDefaultToImperial(result);
    }

    return result;
  }

  public async updateMilestoneInfo(uid: string, milestoneInfo: any) {
    const lastBodyScan = await this.bodyScanRepository.getLastBodyScan(uid);
    if (!lastBodyScan) {
      return;
    }
    return await this.bodyScanRepository.updateScanWithTargetAndMilestone(
      lastBodyScan.id,
      lastBodyScan.target,
      milestoneInfo,
    );
  }

  public async resetWeightTarget(uid: string) {
    const lastBodyScan = await this.bodyScanRepository.getLastBodyScan(uid);
    if (!lastBodyScan) {
      return;
    }

    if (lastBodyScan.target && !lastBodyScan.target.reached) {
      await this.bodyScanRepository.updateScanWithTargetAndMilestone(lastBodyScan.id, undefined, undefined);
    }
  }

  public convertTargetInfoToImperialIfRequired(
    weightTargetInfo: IWeightTargetInfo,
    user: UserProfile,
    userTargetType: UserTargetTypes,
  ): IWeightTargetInfo {
    if (!user.useImperialUnit) {
      return weightTargetInfo;
    }

    if (
      weightTargetInfo.currentWeight
      && weightTargetInfo.target?.targetType !== UserTargetTypes.Bodyfat
    ) {
      weightTargetInfo.currentWeight =
        MetricToImperialConverter.convertWeightToImperial(weightTargetInfo.currentWeight);
    }

    if (userTargetType === UserTargetTypes.Musclemass) {
      const target = weightTargetInfo.target;
      if (target && target.muscleMass) {
        target.muscleMass = MetricToImperialConverter.convertWeightToImperial(target.muscleMass);
        if (target.muscleMassChange) {
          target.muscleMassChange = MetricToImperialConverter.convertWeightToImperial(target.muscleMassChange);
        }
        target.unit = MassUnit.Pound;
      }
      const milestone = weightTargetInfo.milestone;
      if (milestone && milestone.muscleMass) {
        milestone.muscleMass = MetricToImperialConverter.convertWeightToImperial(milestone.muscleMass);
        milestone.unit = MassUnit.Pound;
      }

      return {
        ...weightTargetInfo,
        target,
        milestone,
      };
    } else {
      const target = weightTargetInfo.target;
      if (target && target.weight) {
        target.weight = MetricToImperialConverter.convertWeightToImperial(target.weight);
        if (target.weightChange) {
          target.weightChange = MetricToImperialConverter.convertWeightToImperial(target.weightChange);
        }
        target.unit = MassUnit.Pound;
      }
      const milestone = weightTargetInfo.milestone;
      if (milestone && milestone.weight) {
        milestone.weight = MetricToImperialConverter.convertWeightToImperial(milestone.weight);
        milestone.unit = MassUnit.Pound;
      }

      return {
        ...weightTargetInfo,
        target,
        milestone,
      };
    }
  }

  public convertMinMaxDefaultToImperial(minMaxDefault: IMinMaxDefault) {
    const convertedMin = MetricToImperialConverter.convertWeightToImperial(minMaxDefault.min);
    const convertedMax = MetricToImperialConverter.convertWeightToImperial(minMaxDefault.max);
    const convertedDefault = MetricToImperialConverter.convertWeightToImperial(minMaxDefault.default);
    return {
      ...minMaxDefault,
      min: convertedMin,
      max: convertedMax,
      default: convertedDefault,
    };
  }

  public async calculateTarget(
    target: number,
    userInfo: Partial<UserProfile>,
    requestTime: Date,
    timezone: string,
    userTargetType: UserTargetTypes,
    targetMetricValues: ITargetMetricValues,
    isCurrentMetric: boolean,
  ) {
    if (!userInfo.height || !userInfo.weight || !userInfo.longTermGoal) {
      throw new Error(`User info is incompleate`);
    }

    const isBodyFatTarget = userTargetType === UserTargetTypes.Bodyfat;
    if (!isBodyFatTarget) {
      target = ensureInputIsInMetricUnits(userInfo, target);
    }

    let result: any;
    if (!userInfo.age) {
      // Depricated (keep for backwards compat)
      result = calcWeightTargetDate(target, userInfo, requestTime, timezone);
    } else {
      result = calcTargetDateBasedOnModel(
        target,
        userInfo,
        requestTime,
        timezone,
        userTargetType,
        targetMetricValues,
        isCurrentMetric,
      );
    }

    if (userInfo.useImperialUnit) {
      if (userTargetType === UserTargetTypes.Musclemass ) {
        result.muscleMassChange = MetricToImperialConverter.convertWeightToImperial(result.muscleMassChange);
        result.muscleMass = MetricToImperialConverter.convertWeightToImperial(result.muscleMass);
        result.unit = MassUnit.Pound;
      } else if (userTargetType === UserTargetTypes.Bodyweight) {
        result.weightChange = MetricToImperialConverter.convertWeightToImperial(result.weightChange);
        result.weight = MetricToImperialConverter.convertWeightToImperial(result.weight);
        result.unit = MassUnit.Pound;
        }
      }

    return result;
  }

  public async getWeeklyNutritionRatingsForUser(uid: string, requestTime: Date, timezone: string) {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(uid);
    return this.getWeeklyNutritionRatings(userProfile, requestTime, timezone);
  }

  public async getWeeklyProgress(user: UserProfile, requestTime: Date, timezone: string): Promise<IWeeklyProgress> {
    const endOfWeek = TimeSpan.getEndOfISOWeekInTimezone(requestTime, timezone);
    const numberOfDaysToConsider = 7 * 4; // 4 weeks data
    const startOfQuery = TimeSpan.getXDaysAgo(numberOfDaysToConsider, endOfWeek);
    const startOfWeek = TimeSpan.getStartOfISOWeekInTimezone(requestTime, timezone);

    const workoutMinuteTotals = await this.userWorkoutStatsRepository.getUserMinutesPerForkout(
      user.uid,
      startOfQuery.getTime(),
      endOfWeek.getTime(),
    );

    const targetBaselineMinutes = WorkoutTargetConfig[user.longTermGoal] || 0;
    const futureWorkouts = workoutMinuteTotals.filter((row) => {
      return row.startTime >= requestTime.getTime() &&
        row.endTime <= endOfWeek.getTime();
    });

    const groupedWorkoutsInWeeks = this.getGroupedWorkoutsInWeeks(
      workoutMinuteTotals,
      requestTime,
      startOfWeek,
      endOfWeek,
      numberOfDaysToConsider,
    );
    const currentWeekWorkouts = groupedWorkoutsInWeeks[groupedWorkoutsInWeeks.length - 1];
    const completedMinutes = this.sumDurationOfWorkoutInMinutes(currentWeekWorkouts);

    // Todo: scheduled minutes is calculated client side.
    //  This data can be removed from the response once all apps are upgraded 02/02/2022
    const scheduledMinutes = this.sumDurationOfWorkoutInMinutes(futureWorkouts);

    const weeklyWorkoutMinutesTarget = this.calculateWeeklyWorkoutMinutesTarget(
      groupedWorkoutsInWeeks,
      targetBaselineMinutes,
      requestTime,
      startOfQuery,
      timezone,
      user,
    );

    return {
      totalTargetMinutes: weeklyWorkoutMinutesTarget,
      completedMinutes,
      scheduledMinutes,
      calendar: [],
    };
  }

  public async getWeeklyProgressAndTargetsForUser(userId: string, requestTime: Date, timezone: string) {
    const endOfWeek = TimeSpan.getEndOfISOWeekInTimezone(requestTime, timezone);
    const numberOfDaysToConsider = 7 * 4; // 4 weeks data
    const startOfQuery = TimeSpan.getXDaysAgo(numberOfDaysToConsider, endOfWeek);
    const startOfWeek = TimeSpan.getStartOfISOWeekInTimezone(requestTime, timezone);
    const workoutMinuteTotals = await this.userWorkoutStatsRepository.getUserMinutesPerForkout(
      userId,
      startOfQuery.getTime(),
      endOfWeek.getTime(),
    );

    const groupedWorkoutsInWeeks = this.getGroupedWorkoutsInWeeks(
      workoutMinuteTotals,
      requestTime,
      startOfWeek,
      endOfWeek,
      numberOfDaysToConsider,
    );
    const { sundays: graphDateLabels, startWeek, currentWeekIndex } = this.getWeeklyProgressSundays(
      groupedWorkoutsInWeeks, requestTime, timezone);

    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new Error(`Failed to load user with id: ${userId}`);
    }

    const positionOfLastWeeklyMinuteTargetChangeOnGraph =
      this.getPositionOfLastWeeklyMinuteTargetChangeOnGraph(
        userProfile,
        graphDateLabels,
        timezone,
      );

    const graphData = this.getWeeklyProgressMinutes(groupedWorkoutsInWeeks, startWeek);

    const lengthOfGraph = 5;
    const currentWeekWithNoDataShift = 3;

    if (currentWeekIndex < currentWeekWithNoDataShift) {
      groupedWorkoutsInWeeks.splice(0, currentWeekWithNoDataShift - currentWeekIndex);
      while (groupedWorkoutsInWeeks.length < lengthOfGraph) {
        groupedWorkoutsInWeeks.push([]);
      }
    }

    const targetData = await this.getWeeklyProgressTargets(
      userProfile,
      groupedWorkoutsInWeeks,
      positionOfLastWeeklyMinuteTargetChangeOnGraph.positionOnGraphOfLastTargetChange,
      positionOfLastWeeklyMinuteTargetChangeOnGraph.target,
      currentWeekIndex,
    );

    const formattedGraphDataLabels: string[] = graphDateLabels.map((gd) => gd.format('D MMM'));

    return {
      graphData,
      graphDateLabels: formattedGraphDataLabels,
      targetData,
      currentWeekIndex,
    };
  }

  public async getWeeklyEffortPoints(userId: string, requestTime: Date, timezone: string) {
    const weeks = 5;
    const graphData: number[] = [];
    let graphDateLabels: string[] = [];
    const toDate = moment(requestTime).tz(timezone).endOf('day');
    const fromDate = toDate.clone().subtract(weeks, 'weeks').startOf('isoWeek');
    const workouts = await this.userWorkoutStatsRepository.getUserMinutesPerForkout(
      userId,
      fromDate.valueOf(),
      toDate.valueOf(),
    );
    const pastAndFutureSundays = this.getPastAndFutureSundays(requestTime, timezone, weeks, weeks);
    const dateAndLoadTotal = pastAndFutureSundays.map((sunday) => {
      const workoutsForWeek = workouts.filter((workout) =>
        moment(workout.endTime).tz(timezone).endOf('isoWeek').valueOf() === sunday.valueOf());
      return [sunday, workoutsForWeek.reduce((a, b: any) => a + b.load, 0)];
    });
    const index = dateAndLoadTotal.findIndex((x) => x[1] > 0);
    const valueAfter = dateAndLoadTotal[index + 1] ? dateAndLoadTotal[index + 1][1] : undefined;
    const valueBefore = dateAndLoadTotal[index - 1] ? dateAndLoadTotal[index - 1][1] : undefined;

    if (valueAfter > 0) {
      dateAndLoadTotal.splice(0, index);
    } else if (valueBefore === 0) {
      dateAndLoadTotal.splice(0, index - 1);
    }

    dateAndLoadTotal.splice(6, dateAndLoadTotal.length);

    dateAndLoadTotal.map((x: any) => {
      graphData.push(x[1]);

      if (index === -1) {
        const startWeek = moment(requestTime).tz(timezone).week();
        const filledWeeks = Array(graphWeeks.length).fill(null).map((_, i) => startWeek + i);
        graphDateLabels = filledWeeks.map((week) => {
          return moment(requestTime).tz(timezone).day('Sunday').week(week).format('D MMM');
        });
      } else {
        graphDateLabels.push(x[0].format('D MMM'));
      }
    });

    return {
      graphData,
      graphDateLabels,
    };
  }

  public async getWorkoutStatsForUser(uid: string, year: number, month: number): Promise<IWorkoutStats> {
    const userProfile = await this.userProfileRepository.get(uid);
    const workoutStatsForSixMonths = await this.getWorkoutStatsForSixMonths(uid, year, month);

    return {
      totals: this.getWorkoutTotals(workoutStatsForSixMonths),
      muscleGroupPercentages: this.getMuscleGroupPercentages(workoutStatsForSixMonths),
      ...this.getWorkoutStats(workoutStatsForSixMonths, userProfile),
    };
  }

  public async getScanStatsForUser(
    uid: string, dateUtc: Date, timezone: string | undefined): Promise<IScanStats | undefined> {
    const userProfile = await this.userProfileRepository.get(uid);
    const mostRecentScanStatsForSixMonths = await this.getUserScanStatsForSixMonths(uid, dateUtc, timezone);
    let scanStatsForUser = this.getScanStats(mostRecentScanStatsForSixMonths, userProfile) as IScanStats | undefined;
    if (userProfile && userProfile.useImperialUnit && scanStatsForUser) {
      scanStatsForUser = MetricToImperialConverter.convertScanStatsToImperial(scanStatsForUser);
    }
    return scanStatsForUser;
  }

  public async getNutritionRatingsForUser(uid: string, date: Date) {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(uid);
    const mostRecentNutritionRatingsForSixMonths = await this.getNutritionRatingsForSixMonths(userProfile, date);
    const nutritionRatingTimeline = this.getNutritionRatingTimeline(userProfile, date);
    const lastNutritionRating = this.getLastNutritionRating(userProfile);

    return this.getNutritionRatings(
      date, mostRecentNutritionRatingsForSixMonths, nutritionRatingTimeline, lastNutritionRating);
  }

  public async getGoalRangesForUserDashboard(uid: string, dateUtc: Date, timezone: string | undefined) {
    const userProfile = await this.userProfileRepository.get(uid);
    const workoutStats = await this.getWorkoutStatsForLastweek(uid, dateUtc, timezone);
    const workoutCount = workoutStats ? workoutStats.length : 0;

    const {
      scanDate,
      requiresCurrentMonthBodyScan,
      currentWeight,
      currentPercentBodyFat,
      error,
      scanType,
    } = await this.getScanStatsForMonthOrLatest(uid, dateUtc, timezone);

    const {
      workoutRangeDegrees, workoutRangePercentages, workoutValuePercent,
    } = this.getWorkoutCountStatRanges(workoutCount);

    const { bodyFatRangeDegrees, bodyFatRangePercentages, bodyFatValuePercent,
      muscleMassRangeDegrees, muscleMassRangePercentages, muscleMassValuePercent, muscleMassMeter, muscleMassMeterKg } =
      this.getScanStatRanges(userProfile, currentWeight, currentPercentBodyFat);

    let stats = {
      workouts: {
        description: 'Last 7 Days` Workouts',
        value: workoutCount,
        rangeDegrees: workoutRangeDegrees,
        rangePercentages: workoutRangePercentages,
        valuePercent: workoutValuePercent,
      },
      totalBodyWeight: {
        description: 'Total Body Weight',
        value: currentWeight ? round(currentWeight) : undefined,
        unit: 'kg',
        error,
      },
      bodyFat: {
        description: 'Body Fat',
        value: currentPercentBodyFat ? round(currentPercentBodyFat) : undefined,
        unit: '%',
        error,
        rangeDegrees: bodyFatRangeDegrees,
        rangePercentages: bodyFatRangePercentages,
        valuePercent: bodyFatValuePercent,
      },
      muscleMass: {
        description: 'Muscle Mass',
        value: muscleMassMeter ? muscleMassMeterKg : undefined,
        unit: 'kg',
        error,
        rangeDegrees: muscleMassRangeDegrees,
        rangePercentages: muscleMassRangePercentages,
        valuePercent: muscleMassValuePercent && round(muscleMassValuePercent),
      },
      scanDate,
      requiresCurrentMonthBodyScan,
    };
    if (scanType === BodyScanTypes.Manual) {
      stats.bodyFat.error = 'Requires a body scan';
      stats.muscleMass.error = 'Requires a body scan';
    }
    if (userProfile && userProfile.useImperialUnit) {
      stats = MetricToImperialConverter.convertGoalTrackingToImperial(stats);
    }

    return stats;
  }

  public async addBodyScaleScanForUser(userProfile: UserProfile, scanDateString: string, rawScan: any,
                                       target?: ITargetInfo, milestone?: ITargetInfo) {
    let bodyAssessmentSnapshot;
    if (userProfile.bodyAssessment) {
      bodyAssessmentSnapshot = userProfile.bodyAssessment;
    }
    const bodyScaleScanStats = await this.calculateBodyScaleScanStatsForUser(
      userProfile, rawScan.weight, rawScan.bodyFatRate);
    // save to bodyscans in compatible format for now (will do migration later)
    const bodyScanData = {
      uid: userProfile.uid,
      Email: userProfile.email,
      userEmail: userProfile.email,
      testTime: scanDateString,
      weight: rawScan.weight,
      rawScan,
      bodyAssessmentSnapshot,
      scanWeight: rawScan.weight,
      calculatedMuscleMassMeter: bodyScaleScanStats.calculatedMuscleMassMeter,
      calculatedMuscleMassMeterKg: bodyScaleScanStats.calculatedMuscleMassMeterKg,
      calculatedBodyFat: bodyScaleScanStats.calculatedBodyFat,
      bodyFatAdjustments: {
        rawPreAdjustmentBodyFat: bodyScaleScanStats.bodyFatAdjustments.rawPreAdjustmentBodyFat,
        athleteAdjustment: bodyScaleScanStats.bodyFatAdjustments.athleteAdjustment,
        bodyTypeAdjustment: bodyScaleScanStats.bodyFatAdjustments.bodyTypeAdjustment,
        safetyNetAdjustment: bodyScaleScanStats.bodyFatAdjustments.safetyNetAdjustment,
        bodyFatProposedTrendLineX: bodyScaleScanStats.bodyFatAdjustments.bodyFatProposedTrendline.x,
        bodyFatProposedTrendLineY: bodyScaleScanStats.bodyFatAdjustments.bodyFatProposedTrendline.y,
        minimumBodyFatCorrectionAdjustment: bodyScaleScanStats.bodyFatAdjustments.minimumBodyFatCorrectionAdjustment,
        adjustedBodyFat: bodyScaleScanStats.bodyFatAdjustments.adjustedBodyFat,
      },
      calculatedBodyType: bodyScaleScanStats.calculatedBodyType,
      analysis: {
        items: [
          {
            itemID: 'WT',
            value: new Array(4).fill(round(rawScan.weight).toString()),
          },
          {
            itemID: 'SMM',
            value: new Array(4).fill(rawScan.skeletalMuscleMass.toString()),
          },
          {
            itemID: 'PBF',
            value: new Array(4).fill(bodyScaleScanStats.calculatedBodyFat.toString()),
          },
        ],
      },
      isLeaone: true,
      target,
      milestone,
    };
    return await this.bodyScanRepository.addBodyScaleScan(bodyScanData);
  }

  public async calculateBodyScaleScanStatsForUser(userProfile: UserProfile, weight: number, bodyFat: number) {
    try {
      let bodyFatAdjustments: IBodyFatAdjustments;
      let calculatedBodyType: any = bodyComposition.meso;
      let calculatedBodyFat = bodyFat;

      if (userProfile && userProfile.bodyAssessment) {
        const bodyAssessment = userProfile.bodyAssessment;
        calculatedBodyType = userProfile.bodyComposition ?
          bodyComposition[userProfile.bodyComposition] : getSomatotypeBasedOnAnswers(bodyAssessment);

        bodyFatAdjustments = BodyFatAdjustmentCalculations.adjustBodyFat(
          bodyFat,
          calculatedBodyType,
          userProfile.height,
          weight,
          userProfile.gender,
          bodyAssessment,
        );

        calculatedBodyFat = bodyFatAdjustments.adjustedBodyFat;
      } else {
        bodyFatAdjustments = BodyFatAdjustmentCalculations.InitialiseBodyFatAdjustments();
      }

      const {
        bodyFatRangeDegrees,
        bodyFatRangePercentages,
        bodyFatValuePercent,
        muscleMassRangeDegrees,
        muscleMassRangePercentages,
        muscleMassValuePercent,
        muscleMassMeter,
        muscleMassMeterKg,
      } = this.getScanStatRanges(userProfile, weight, calculatedBodyFat);

      const { bodyFatRangeWeights } =
        this.getBodyWeightRanges(userProfile, weight, calculatedBodyFat);

      const totalBodyWeight = {
        description: 'Total Body Weight',
        valAndUnit: weight ? `${round(weight)}kg` : undefined,
        rangeDegrees: bodyFatRangeDegrees,
        rangePercentages: bodyFatRangePercentages,
        rangeValues: bodyFatRangeWeights,
        valuePercent: bodyFatValuePercent,
      };
      const muscleMass = {
        description: 'Muscle Mass',
        valAndUnit: muscleMassMeter ? `${muscleMassMeterKg}kg` : undefined,
        rangeDegrees: muscleMassRangeDegrees,
        rangePercentages: muscleMassRangePercentages,
        valuePercent: muscleMassValuePercent && round(muscleMassValuePercent),
      };
      const fat = {
        description: 'Fat %',
        valAndUnit: calculatedBodyFat ? `${round(calculatedBodyFat)}%` : undefined,
        rangeDegrees: bodyFatRangeDegrees,
        rangePercentages: bodyFatRangePercentages,
        valuePercent: bodyFatValuePercent,
      };

      let calculatedBodyScaleScanStats = {
        calculatedMuscleMassMeter: muscleMassMeter,
        calculatedMuscleMassMeterKg: muscleMassMeterKg,
        calculatedBodyFat,
        bodyFatAdjustments: {
          rawPreAdjustmentBodyFat: bodyFat,
          athleteAdjustment: bodyFatAdjustments.athleteAdjustment,
          bodyTypeAdjustment: bodyFatAdjustments.bodyTypeAdjustment,
          safetyNetAdjustment: bodyFatAdjustments.safetyNetAdjustment,
          minimumBodyFatCorrectionAdjustment: bodyFatAdjustments.minimumBodyFatCorrectionAdjustment,
          adjustedBodyFat: bodyFatAdjustments.adjustedBodyFat,
          bodyFatProposedTrendline: {
            x: bodyFatAdjustments.proposedTrendLine.x,
            y: bodyFatAdjustments.proposedTrendLine.y,
          },
        },
        calculatedBodyType,
        totalBodyWeight,
        muscleMass,
        fat,
        weight,
      };

      if (userProfile && userProfile.useImperialUnit) {
        calculatedBodyScaleScanStats = MetricToImperialConverter.
          convertCalculatedBodyScaleScanStats(calculatedBodyScaleScanStats);
      }

      return calculatedBodyScaleScanStats;
    } catch (error) {
      const gender = userProfile ? userProfile.gender : 'unspecified';
      const height = userProfile ? userProfile.height : 'unspecified';
      const bodyType = userProfile ? userProfile.bodyComposition : 'unspecified';
      this.logger.error(`Failed to calculate body scale scan stats for user with gender: ${gender} ` +
        `and height: ${height} and body type: ${bodyType} and raw body fat: ${bodyFat}.`);
      throw error;
    }
  }

  public async determineBodyComposition(bodyAssessment: IBodyAssessment) {
    let derivedBodyComposition: bodyComposition = bodyComposition.meso;
    derivedBodyComposition = getSomatotypeBasedOnAnswers(bodyAssessment);
    return derivedBodyComposition;
  }

  public async getLastWorkoutStats(uid: string, requestTime: Date, timezone: string) {
    const to = moment(requestTime).tz(timezone);
    const lastworkout = await this.workoutRepository.getLastWorkout(uid, new Date(2018, 1, 1), to.toDate());
    let lastWorkoutMinutes = 0;

    if (lastworkout) {
      const totalMinutes = calcTotalMinutes(lastworkout);
      lastWorkoutMinutes = getTotalMinutesCeil(lastworkout.workoutType, totalMinutes);
    }

    const lastWorkoutDetails = lastworkout ?
      `${moment(lastworkout.startTimeOfFirstExercise).tz(timezone)
        .format('ddd, D MMM, HH:mm')} | ${lastWorkoutMinutes}min`
      : null;

    let effortPoints;
    let calories;
    let averageEffort;
    const workoutSettings = (lastworkout && lastworkout.settings) ? lastworkout.settings : null;
    if (lastworkout && lastworkout.performanceMetrics) {
      effortPoints = lastworkout.performanceMetrics.load ? lastworkout.performanceMetrics.load : 0;
      calories = lastworkout.performanceMetrics.calories ? lastworkout.performanceMetrics.calories : 0;
      averageEffort = lastworkout.performanceMetrics.averageEffort ?
        lastworkout.performanceMetrics.averageEffort : 0;
    }

    return {
      lastWorkoutDetails,
      effortPoints,
      calories,
      averageHeartRate: averageEffort,
      workoutSettings,
    };
  }

  public getBodyweightTargets(bodyMetricData: any) {
    const targets = ({
      complete: bodyMetricData.hasAScan ?
        bodyMetricData.complete.map(({ date, value }: any) => ({
          date: moment(date).format('DD MMM'),
          value: Math.round(value * 10) / 10,
        })).slice(-1)[0] : null,
      milestone: {
        date: moment(bodyMetricData.milestone.date).format('DD MMM'),
        value: Math.round(bodyMetricData.milestone.value * 10) / 10,
        change: 0,
      },
      ...(bodyMetricData.target && {
        target: {
          date: moment(bodyMetricData.target.date).format('DD MMM'),
          value: Math.round(bodyMetricData.target.value * 10) / 10,
          change: 0,
        },
      }),
    });

    if (targets && targets.complete && targets.milestone) {
      targets.milestone.change =
        Math.round((targets.complete.value - targets.milestone.value) * 10) / 10;
    }
    if (targets && targets.complete && targets.target && targets.target.value) {
      targets.target.change =
        Math.round((targets.complete.value - targets.target.value) * 10) / 10;
    }
    return targets;
  }

  public getBodyMetricRanges(user: UserProfile, bodyScans: IBodyScansAndLatest) {
    const scans = bodyScans.scans.slice();
    const latestBodyScanWithScale = scans.reverse().find((scan) => scan.type !== BodyScanTypes.Manual);

    if (!latestBodyScanWithScale || !latestBodyScanWithScale.bodyweight || !latestBodyScanWithScale.bodyfat) {
      return undefined;
    }

    const { bodyFatStatRangeValues,
      muscleMassRangeValues } =
      this.getScanStatRanges(user, latestBodyScanWithScale.bodyweight, latestBodyScanWithScale.bodyfat);

    const { bodyFatRangeWeights } = this.getBodyWeightRanges(
      user,
      latestBodyScanWithScale.bodyweight,
      latestBodyScanWithScale.bodyfat,
    );

    let convertedBodyWeightRanges = bodyFatRangeWeights;
    let convertedMuscleMassRanges = muscleMassRangeValues;

    if (user && user.useImperialUnit) {
      convertedBodyWeightRanges = MetricToImperialConverter.convertBodyMetricRanges(bodyFatRangeWeights);
      convertedMuscleMassRanges = MetricToImperialConverter.convertBodyMetricRanges(muscleMassRangeValues);
    }

    return {
      bodyFatRanges: bodyFatStatRangeValues,
      muscleMassRanges: convertedMuscleMassRanges,
      bodyWeightRanges: convertedBodyWeightRanges,
    };
  }

  public async getTargetChangeData(
    uid: string,
    requestTime: Date,
    timezone: string) {
      const user = await this.userProfileRepository.get(uid);
      if (!user) {
        throw new Error(`Failed to load user with id: ${uid}`);
      }

      const lastBodyScan = await this.bodyScanRepository.getLastBodyScan(uid);
      if (!lastBodyScan) {
        throw new Error(`No scan found for user: ${uid}`);
      }

      const mappedScan = this.mapScansToBodyScanModel(lastBodyScan);
      if (!mappedScan.target || !mappedScan.target.targetType) {
        throw new Error(`Invaid target for user: ${uid}, last body scan id: ${lastBodyScan.id}`);
      }

      const userTargetType = mappedScan.target.targetType;

      let minMaxDefault = calcMinMaxDefaulTarget(
        user,
        requestTime,
        timezone,
        mappedScan.target.targetType,
        lastBodyScan,
      );
      const isBodyFatTarget = userTargetType === UserTargetTypes.Bodyfat;
      if (user.useImperialUnit && !isBodyFatTarget) {
        minMaxDefault = this.convertMinMaxDefaultToImperial(minMaxDefault);
      }

      const weightTargetInfo = this.weightTargetInfoFromScan(user, mappedScan);
      this.convertTargetInfoToImperialIfRequired(weightTargetInfo, user, userTargetType);

      const scaleScanStats = await this.calculateBodyScaleScanStatsForUser(
        user,
        mappedScan.bodyweight!,
        lastBodyScan.rawBodyFat ? lastBodyScan.rawBodyFat : 0,
      );

      return {minMaxDefault, weightTargetInfo, scaleScanStats};
  }

  private weightTargetInfoFromScan(user: UserProfile, latestScan?: IBodyScanModel) {
    const weightTargetInfo: IWeightTargetInfo = {
      target: undefined,
      milestone: undefined,
      currentWeight: undefined,
    };

    if (!latestScan) {
      return weightTargetInfo;
    }

    let unit: string | MassUnit;
    let userTargetType;

    if (latestScan.target) {
      userTargetType = latestScan.target.targetType;
      unit = (userTargetType === UserTargetTypes.Bodyfat) ? '%' : MassUnit.Killogram;

      weightTargetInfo.target = latestScan.target;
      weightTargetInfo.target.date = new Date(weightTargetInfo.target.date);
      weightTargetInfo.target.unit = unit;

      switch (latestScan.target.targetType) {
        case UserTargetTypes.Musclemass:
          weightTargetInfo.currentWeight = latestScan.musclemass;
          break;
        case UserTargetTypes.Bodyweight:
          weightTargetInfo.currentWeight = latestScan.bodyweight;
          break;
        case UserTargetTypes.Bodyfat:
          weightTargetInfo.currentWeight = latestScan.bodyfat;
          break;
      }

      if (latestScan.milestone) {
        weightTargetInfo.milestone = latestScan.milestone;
        weightTargetInfo.milestone.date = new Date(weightTargetInfo.milestone.date);
        weightTargetInfo.milestone.unit = unit;
      }
    }

    return weightTargetInfo;
  }

  private getTargetTypeFromTarget(bodyScan: IBodyScanModel) {
    if (bodyScan && bodyScan.target && bodyScan.target.targetType) {
      return bodyScan.target.targetType;
    }

    return undefined;
  }

  private getBodyMetricSetForUser(
    user: UserProfile,
    bodyScans: IBodyScansAndLatest,
    historyFromDate: any,
    hasReachedTargetWithLastReview: boolean = false,
  ) {
    const bodyMetricRanges = this.getBodyMetricRanges(user, bodyScans);

    let userTargetType = this.getTargetTypeFromTarget(bodyScans.latest);

    if (!userTargetType) {
      userTargetType = this.getDefaultUserTargetType(user);
    }

    let bodyMetricSet;
    let targetAndHistory;
    let latestTarget;
    let latestTargetIsNotReached;

    // Rules:
    // bodyMetricType of Bodyweight is manual weight log
    // bodyMetricType of Bodyfat is smart scale log on long term goal GetLeanAndFitGoal or GetStrongAndFitGoal
    // bodyMetricType of Musclemass is smart scale log on long term goal GainMuscleAndStrengthGoal
    switch (userTargetType) {
      case UserTargetTypes.Bodyweight:
        latestTarget = this.getLatestTarget(bodyScans.latest, UserTargetTypes.Bodyweight);
        latestTargetIsNotReached = (latestTarget && !latestTarget.reached) ? true : false;

        if (latestTargetIsNotReached || hasReachedTargetWithLastReview) {
          targetAndHistory = this.getBodyMetricTargetAndHistory(
            UserTargetTypes.Bodyweight,
            bodyScans,
            user,
            bodyMetricRanges ? bodyMetricRanges.bodyWeightRanges : undefined,
            hasReachedTargetWithLastReview,
          );
        }

        bodyMetricSet = [
          this.getBodyMetricHistory(
            UserTargetTypes.Bodyweight,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.bodyWeightRanges : undefined,
          ),
          this.getBodyMetricHistory(
            UserTargetTypes.Bodyfat,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.bodyFatRanges : undefined,
          ),
          this.getBodyMetricHistory(
            UserTargetTypes.Musclemass,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.muscleMassRanges : undefined,
          ),
        ];

        if (targetAndHistory) {
          bodyMetricSet.unshift(targetAndHistory);
        }

        return bodyMetricSet;
      case UserTargetTypes.Bodyfat:
        latestTarget = this.getLatestTarget(bodyScans.latest, UserTargetTypes.Bodyfat);
        latestTargetIsNotReached = (latestTarget && !latestTarget.reached) ? true : false;

        if (latestTargetIsNotReached || hasReachedTargetWithLastReview) {
          targetAndHistory = this.getBodyMetricTargetAndHistory(
            UserTargetTypes.Bodyfat,
            bodyScans,
            user,
            bodyMetricRanges ? bodyMetricRanges.bodyFatRanges : undefined,
            hasReachedTargetWithLastReview,
          );
        }

        bodyMetricSet = [
          this.getBodyMetricHistory(
            UserTargetTypes.Bodyfat,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.bodyFatRanges : undefined,
          ),
          this.getBodyMetricHistory(
            UserTargetTypes.Bodyweight,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.bodyWeightRanges : undefined,
          ),
          this.getBodyMetricHistory(
            UserTargetTypes.Musclemass,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.muscleMassRanges : undefined,
          ),
        ];

        if (targetAndHistory) {
          bodyMetricSet.unshift(targetAndHistory);
        }

        return bodyMetricSet;
      case UserTargetTypes.Musclemass:
        latestTarget = this.getLatestTarget(bodyScans.latest, UserTargetTypes.Musclemass);
        latestTargetIsNotReached = (latestTarget && !latestTarget.reached) ? true : false;
        if (latestTargetIsNotReached || hasReachedTargetWithLastReview) {
          targetAndHistory = this.getBodyMetricTargetAndHistory(
            UserTargetTypes.Musclemass,
            bodyScans,
            user,
            bodyMetricRanges ? bodyMetricRanges.muscleMassRanges : undefined,
          );
        }

        bodyMetricSet = [
          this.getBodyMetricHistory(
            UserTargetTypes.Musclemass,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.muscleMassRanges : undefined,
          ),
          this.getBodyMetricHistory(
            UserTargetTypes.Bodyweight,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.bodyWeightRanges : undefined,
          ),
          this.getBodyMetricHistory(
            UserTargetTypes.Bodyfat,
            bodyScans,
            historyFromDate,
            user,
            bodyMetricRanges ? bodyMetricRanges.bodyFatRanges : undefined,
          ),
        ];

        if (targetAndHistory) {
          bodyMetricSet.unshift(targetAndHistory);
        }

        return bodyMetricSet;
      default:
        return null;
    }
  }

  private getDefaultUserTargetType(userProfile: any): UserTargetTypes {
    return userProfile.longTermGoal === ProgramType.GainMuscleAndStrengthGoal
      ? UserTargetTypes.Musclemass
      : UserTargetTypes.Bodyweight;
  }

  private recalculateWeightTargetDate(
    user: UserProfile,
    latestBodyscan: any,
    requestTime: Date,
    timezone: string,
    newWeight: number,
  ) {
    const userTargetType = UserTargetTypes.Bodyweight;
    const targetAndMilestoneInfo: any = {};
    if (latestBodyscan.target) {
      targetAndMilestoneInfo.target = latestBodyscan.target;
      if (newWeight <= latestBodyscan.target.weight) {
        const reachedDate = moment(requestTime).tz(timezone);
        targetAndMilestoneInfo.target.reached = true;
        targetAndMilestoneInfo.target.weight = newWeight;
        targetAndMilestoneInfo.target.date = reachedDate.toDate();
        targetAndMilestoneInfo.target.shortDate = reachedDate.format('DD MMM');
        targetAndMilestoneInfo.target.monthsNeeded = 0;
        targetAndMilestoneInfo.target.weightChange = 0;
        targetAndMilestoneInfo.target.duration = undefined;
      } else {
        const targetDateInfo = calcTargetDateBasedOnModel(
          latestBodyscan.target.weight,
          {
            weight: newWeight,
            height: user.height,
            age: user.age,
            gender: user.gender,
            longTermGoal: user.longTermGoal,
            skillLevel: user.skillLevel,
            bodyComposition: user.bodyComposition,
          },
          requestTime,
          timezone,
          userTargetType,
          undefined,
        );

        targetAndMilestoneInfo.target = {
          targetType: userTargetType,
          weight: targetAndMilestoneInfo.target.weight,
          reached: false,
          date: targetDateInfo.date,
          shortDate: targetDateInfo.shortDate,
          monthsNeeded: targetDateInfo.monthsNeeded,
          duration: targetDateInfo.duration,
          weightChange: targetDateInfo.weightChange,
          unit: user.useImperialUnit ? MassUnit.Pound : MassUnit.Killogram,
          dailyRateOfChange: targetDateInfo.dailyRateOfChange,
        };
      }

      if (!targetAndMilestoneInfo.target.reached) {
        const fromDateForMilestoneCalc = moment(requestTime).tz(timezone);
        targetAndMilestoneInfo.milestone = this.calculateMilestoneFromTarget(
          fromDateForMilestoneCalc.toDate(),
          targetAndMilestoneInfo.target,
          newWeight,
          userTargetType,
        );

        if (latestBodyscan.milestone && latestBodyscan.milestone.weight) {
          targetAndMilestoneInfo.milestone.reached =
            newWeight <= latestBodyscan.milestone.weight ? true : false;
        }
      }

      return targetAndMilestoneInfo;
    }
  }

  private recalculateMuscleMassTargetDate(
    user: UserProfile,
    latestBodyscan: any,
    requestTime: Date,
    timezone: string,
    targetMetricValues: ITargetMetricValues | undefined,
  ) {
    if (!targetMetricValues) {
      throw new Error(`Scanned muscle mass info is incomplete`);
    }

    const userTargetType = UserTargetTypes.Musclemass;
    const newMuscleMass = targetMetricValues.muscleMass;

    const targetAndMilestoneInfo: any = {};
    if (latestBodyscan.target && latestBodyscan.target) {
      targetAndMilestoneInfo.target = latestBodyscan.target;
      if (targetMetricValues.muscleMass >= latestBodyscan.target.muscleMass) {
        const reachedDate = moment(requestTime).tz(timezone);
        targetAndMilestoneInfo.target.reached = true;
        targetAndMilestoneInfo.target.muscleMass = newMuscleMass;
        targetAndMilestoneInfo.target.date = reachedDate.toDate();
        targetAndMilestoneInfo.target.shortDate = reachedDate.format('DD MMM');
        targetAndMilestoneInfo.target.monthsNeeded = 0;
        targetAndMilestoneInfo.target.muscleMassChange = 0;
        targetAndMilestoneInfo.target.duration = undefined;
      } else {
        const targetDateInfo = calcTargetDateBasedOnModel(
          latestBodyscan.target.muscleMass,
          {
            weight: user.weight,
            height: user.height,
            age: user.age,
            gender: user.gender,
            longTermGoal: user.longTermGoal,
            skillLevel: user.skillLevel,
            bodyComposition: user.bodyComposition,
          },
          requestTime,
          timezone,
          userTargetType,
          targetMetricValues,
        );

        targetAndMilestoneInfo.target = {
          targetType: targetAndMilestoneInfo.target.targetType,
          muscleMass: targetAndMilestoneInfo.target.muscleMass,
          reached: false,
          date: targetDateInfo.date,
          shortDate: targetDateInfo.shortDate,
          monthsNeeded: targetDateInfo.monthsNeeded,
          duration: targetDateInfo.duration,
          muscleMassChange: targetDateInfo.muscleMassChange,
          unit: user.useImperialUnit ? MassUnit.Pound : MassUnit.Killogram,
          dailyRateOfChange: targetDateInfo.dailyRateOfChange,
        };
      }
    }

    if (!targetAndMilestoneInfo.target.reached) {
      const fromDateForMilestoneCalc = moment(requestTime).tz(timezone);
      targetAndMilestoneInfo.milestone = this.calculateMilestoneFromTarget(
        fromDateForMilestoneCalc.toDate(), targetAndMilestoneInfo.target, user.weight, userTargetType);

      if (latestBodyscan.milestone && latestBodyscan.milestone.muscleMass) {
        targetAndMilestoneInfo.milestone.reached =
          newMuscleMass >= latestBodyscan.milestone.muscleMass ? true : false;
      }
    }

    return targetAndMilestoneInfo;
  }

  private recalculateBodyFatTargetDate(
    user: UserProfile,
    latestBodyscan: any,
    requestTime: Date,
    timezone: string,
    targetMetricValues: ITargetMetricValues | undefined,
  ) {
    if (!targetMetricValues) {
      throw new Error(`Scan info is incomplete`);
    }

    const userTargetType = UserTargetTypes.Bodyfat;
    const newBodyFat = targetMetricValues.bodyFat;

    const targetAndMilestoneInfo: any = {};
    if (latestBodyscan.target) {
      targetAndMilestoneInfo.target = latestBodyscan.target;
      if (newBodyFat <= latestBodyscan.target.bodyFat) {
        const reachedDate = moment(requestTime).tz(timezone);
        targetAndMilestoneInfo.target.reached = true;
        targetAndMilestoneInfo.target.bodyFat = newBodyFat;
        targetAndMilestoneInfo.target.date = reachedDate.toDate();
        targetAndMilestoneInfo.target.shortDate = reachedDate.format('DD MMM');
        targetAndMilestoneInfo.target.monthsNeeded = 0;
        targetAndMilestoneInfo.target.bodyFatChange = 0;
        targetAndMilestoneInfo.target.duration = undefined;
      } else {
        const targetDateInfo = calcTargetDateBasedOnModel(
          latestBodyscan.target.bodyFat,
          {
            weight: user.weight,
            height: user.height,
            age: user.age,
            gender: user.gender,
            longTermGoal: user.longTermGoal,
            skillLevel: user.skillLevel,
            bodyComposition: user.bodyComposition,
          },
          requestTime,
          timezone,
          userTargetType,
          targetMetricValues,
        );

        targetAndMilestoneInfo.target = {
          targetType: targetAndMilestoneInfo.target.targetType,
          bodyFat: targetAndMilestoneInfo.target.bodyFat,
          reached: false,
          date: targetDateInfo.date,
          shortDate: targetDateInfo.shortDate,
          monthsNeeded: targetDateInfo.monthsNeeded,
          duration: targetDateInfo.duration,
          bodyFatChange: targetDateInfo.bodyFatChange,
          unit: '%',
          dailyRateOfChange: targetDateInfo.dailyRateOfChange,
        };
      }
    }

    if (!targetAndMilestoneInfo.target.reached) {
      const fromDateForMilestoneCalc = moment(requestTime).tz(timezone);
      targetAndMilestoneInfo.milestone = this.calculateMilestoneFromTarget(
        fromDateForMilestoneCalc.toDate(), targetAndMilestoneInfo.target, user.weight, userTargetType);

      if (latestBodyscan.milestone && latestBodyscan.milestone.bodyFat) {
        targetAndMilestoneInfo.milestone.reached =
          newBodyFat <= latestBodyscan.milestone.bodyFat ? true : false;
      }
    }

    return targetAndMilestoneInfo;
  }

  private updateUserWeightSynchronous(user: UserProfile, newWeight: number, PALValue: number) {
    user.weight = newWeight;
    user.PALValue = PALValue;
    this.userProfileRepository.update(user)
      .catch((e) => this.logger.warn('failed to updateUserWeightSynchronous', e));
  }

  private async logManualWeight(
    uid: string, logDate: string, newWeight: any,
    target?: ITargetInfo, milestone?: ITargetInfo) {
    const manualWeightLog = {
      uid,
      weight: newWeight,
      testTime: logDate,
      analysis: {
        items: [
          {
            itemID: 'WT',
            value: new Array(4).fill(newWeight.toString()),
          },
          {
            itemID: 'SMM',
            value: new Array(4).fill('0'),
          },
          {
            itemID: 'PBF',
            value: new Array(4).fill('0'),
          },
        ],
      },
      isManualWeightLog: true,
      target,
      milestone,
    };
    return await this.bodyScanRepository.addManualWeightLog(manualWeightLog);
  }

  private async getBodyScans(user: UserProfile, requestTime: Date, timezone: string) {
    const now = Date.now();
    let scanStats = await this.getUserBodyScansForRange(user.uid, requestTime, timezone, maxHistoricMonths);
    if (!scanStats || scanStats.length === 0) {
      scanStats = [];
    }
    const scans: IBodyScanModel[] = scanStats.map((scan) => this.mapScans(user, scan));
    this.logger.info(`getBodyScans took ${Date.now() - now}ms to load ${scans.length} scans`);
    return {
      scans,
      latest: scans.slice(-1)[0],
    };
  }

  private mapScans(user: UserProfile, scan: IBodyScanStat) {
    const bodyweight = scan.analysis[ScanStat.Weight] ? scan.analysis[ScanStat.Weight]!.currentValue : 0;
    const bodyfat = scan.analysis[ScanStat.PercentBodyFat] ? scan.analysis[ScanStat.PercentBodyFat]!.currentValue : 0;
    const { muscleMassMeterKg } = this.getScanStatRanges(user, bodyweight, bodyfat);
    return {
      date: new Date(scan.dateTime),
      month: moment(scan.dateTime).month() + 1,
      type: scan.type,
      bodyweight,
      bodyfat,
      musclemass: muscleMassMeterKg,
      target: scan.target,
      milestone: scan.milestone,
    };
  }

  private mapScansToBodyScanModel(scan: IBodyScanData): IBodyScanModel {
    return {
      date: new Date(scan.dateOfScan),
      month: moment(scan.dateOfScan).month() + 1,
      type: scan.type,
      bodyweight: scan.weight,
      bodyfat: scan.calculatedBodyFat,
      musclemass: scan.calculatedMuscleMassMeterKg,
      target: scan.target,
      milestone: scan.milestone,
    };
  }

  private getBodyMetricTargetAndHistory(
    targetAndHistoryType: UserTargetTypes,
    bodyScans: IBodyScansAndLatest,
    user: UserProfile,
    bodyMetricRanges?: any,
    hasReachedTargetWithLastReview: boolean = false,
  ): IBodyMetricModel {
    const maxHistoryOnReachedTarget = 4;
    let graphType: BodyMetricGraphTypes = BodyMetricGraphTypes.BodyweightTargetAndHistory;
    const useImperialUnit = user.useImperialUnit;
    let unit: MassUnit | string = useImperialUnit ? MassUnit.Pound : MassUnit.Killogram;

    switch (targetAndHistoryType) {
      case UserTargetTypes.Musclemass:
        graphType = BodyMetricGraphTypes.MusclemassTargetAndHistory;
        break;
      case UserTargetTypes.Bodyweight:
        graphType = BodyMetricGraphTypes.BodyweightTargetAndHistory;
        break;
      case UserTargetTypes.Bodyfat:
        graphType = BodyMetricGraphTypes.BodyfatTargetAndHistory;
        unit = '%';
        break;
    }

    const result = {
      graphType,
      data: [],
      verticalGridLines: [],
      minDate: new Date(),
      maxDate: new Date(),
      clippingPoint: null,
      mileStones: null,
      unit,
      ranges: bodyMetricRanges,
    };

    if (!bodyScans.latest) {
      return result;
    }

    const latestTarget = this.getLatestTarget(bodyScans.latest, targetAndHistoryType);
    let target: ITargetInfoViewModel | undefined;
    let remainingMonthsForHistory = maxHistoricMonths;
    let targetMonthsNeeded = 0;
    if (latestTarget) {
      target = {
        date: latestTarget.date,
        value: latestTarget.value,
        reached: latestTarget.reached,
        targetMonthsNeeded: latestTarget.targetMonthsNeeded,
      };

      targetMonthsNeeded = target.targetMonthsNeeded!;

      remainingMonthsForHistory = targetMonthsNeeded + 1;
      if (remainingMonthsForHistory <= 0) {
        remainingMonthsForHistory = 1;
      }

      if (latestTarget.reached) {
        remainingMonthsForHistory = 4;
      }
    }
    let startFrom = moment(bodyScans.latest.date).startOf('month').subtract(remainingMonthsForHistory, 'months');
    let filteredScans = bodyScans.scans.filter((scan) => {
      if (moment(scan.date).isBefore(startFrom)) {
        return moment(scan.date).isSameOrAfter(moment(scan.date));
      }
      return moment(scan.date).isAfter(startFrom);
    });

    if (targetAndHistoryType !== UserTargetTypes.Bodyweight) {
      filteredScans = filteredScans
      .filter((scan) => scan.type !== BodyScanTypes.Manual);
    }

    const completedScans = filteredScans.length > 0 ? filteredScans : [];
    const hasScansBefore = bodyScans.scans.some((scan) => {
      return moment(scan.date).isBefore(startFrom);
    });
    if (hasScansBefore) {
      remainingMonthsForHistory = remainingMonthsForHistory - targetMonthsNeeded;
    }
    if (!(target && target.hasOwnProperty('reached') && target.reached)) {
      startFrom = startFrom.add(remainingMonthsForHistory, 'months');
    }

    if (hasReachedTargetWithLastReview) {
      const numberOfHistoricMonthsWithScans =
        Math.min(moment(bodyScans.latest.date).diff(bodyScans.scans[0].date, 'months'), maxHistoryOnReachedTarget);
      startFrom = startFrom.subtract(numberOfHistoricMonthsWithScans, 'months');
    }

    const dateRange = [
      startFrom.toDate(),
      target ? target.date : bodyScans.latest.date];

    const verticalGridLineDates = this.getBodyMetricMonths(dateRange);
    const milestone: ITargetInfoViewModel | null = this.getLatestMilestone(bodyScans.latest, targetAndHistoryType);

    let complete: any[] = [];

    switch (targetAndHistoryType) {
      case UserTargetTypes.Musclemass:
        complete = completedScans.map((x) => ({ date: x.date, value: x.musclemass }));
        break;
      case UserTargetTypes.Bodyweight:
        complete = completedScans.map((x) => ({ date: x.date, value: x.bodyweight }));
        break;
      case UserTargetTypes.Bodyfat:
        complete = completedScans.map((x) => ({ date: x.date, value: x.bodyfat }));
        break;
    }

    const addTargetAndMilestones = () => {
      if (complete.length === 0) {
        return [];
      }

      let newData: any = complete.map(({ date, value }) => ({
        date: new Date(date),
        value: value && this.convertValuesForBodyMetricTargetAndHistory(targetAndHistoryType, useImperialUnit, value),
      }));
      if (milestone) {
        if (!(target && target.hasOwnProperty('reached') && target.reached)) {
          newData = [...newData, {
            date: new Date(milestone.date),
            value: this.convertValuesForBodyMetricTargetAndHistory(
              targetAndHistoryType,
              useImperialUnit,
              milestone.value!,
            ),
            isMileStone: true,
          }];
        }
      }
      if (target) {
        if (target.reached) {
          newData[newData.length - 1].isTarget = true;
        } else {
          newData = [...newData, {
            date: new Date(target.date),
            value: this.convertValuesForBodyMetricTargetAndHistory(
              targetAndHistoryType,
              useImperialUnit,
              target.value!,
            ),
            isTarget: true,
          }];
        }
      }
      return newData;
    };

    return {
      ...result,
      data: addTargetAndMilestones(),
      verticalGridLines: verticalGridLineDates,
      mileStones: milestone ? [milestone] : [],
      minDate: new Date(verticalGridLineDates[0]),
      maxDate: new Date(verticalGridLineDates[verticalGridLineDates.length - 1]),
      clippingPoint: target ? new Date(complete[complete.length - 1].date) : null,
    };
  }

  private convertValuesForBodyMetricTargetAndHistory(
    userTargetType: UserTargetTypes,
    useImperialUnit: boolean | undefined,
    value: number | undefined,
  ) {
    if (userTargetType === UserTargetTypes.Bodyfat) {
      return value;
    }

    return useImperialUnit
      ? MetricToImperialConverter.convertWeightToImperial(value!)
      : value;
  }

  private getBodyMetricHistory(
    historyType: UserTargetTypes,
    bodyScans: IBodyScansAndLatest,
    fromDate: moment.Moment,
    user: UserProfile,
    bodyMetricRanges?: any,
  ): IBodyMetricModel | null {
    const useImperialUnit = user.useImperialUnit;
    const scans = bodyScans.scans
      .filter((scan) => moment(scan.date).isAfter(fromDate))
      .filter((scan) => scan.type !== BodyScanTypes.Manual);
    const earliestScan = scans.length === 0 ? { date: fromDate.toDate() } : scans[0];
    const latestScan = scans.length === 0 ? { date: fromDate.toDate(), type: null } : scans.slice(-1)[0];
    const dateRange = [earliestScan.date, latestScan.date];
    const verticalGridLineDates = this.getBodyMetricMonths(dateRange);
    const valueMap = new Map();
    valueMap.set(UserTargetTypes.Bodyweight, 'bodyweight');
    valueMap.set(UserTargetTypes.Bodyfat, 'bodyfat');
    valueMap.set(UserTargetTypes.Musclemass, 'musclemass');

    const complete = scans.map((scan) => ({
      date: scan.date,
      value: scan[valueMap.get(historyType)],
    }));

    const graphTypeMap = new Map();
    graphTypeMap.set(UserTargetTypes.Bodyweight, BodyMetricGraphTypes.BodyweightHistory);
    graphTypeMap.set(UserTargetTypes.Bodyfat, BodyMetricGraphTypes.BodyfatHistory);
    graphTypeMap.set(UserTargetTypes.Musclemass, BodyMetricGraphTypes.MusclemassHistory);

    const isBodyFat = historyType === UserTargetTypes.Bodyfat;
    const unit: string = isBodyFat ? '%' : useImperialUnit ? MassUnit.Pound : MassUnit.Killogram;
    const shouldConvertToImperial = useImperialUnit && !isBodyFat;

    return {
      graphType: graphTypeMap.get(historyType),
      data: complete.map(({ date, value }) => ({
        date: new Date(date),
        value: (shouldConvertToImperial) ?
          MetricToImperialConverter.convertAmount(MassUnit.Killogram, MassUnit.Pound, value) : value,
      })),
      verticalGridLines: verticalGridLineDates,
      minDate: new Date(verticalGridLineDates[0]),
      maxDate: new Date(verticalGridLineDates[verticalGridLineDates.length - 1]),
      clippingPoint: null,
      mileStones: null,
      unit,
      ranges: bodyMetricRanges,
    };
  }

  private getBodyMetricMonths(dateRange: Date[]) {
    const dateRangeMoments = dateRange.map((x) => moment(x));
    const [start, end] = dateRangeMoments;
    const endPlusOne = end.add(1, 'month');
    const interim = start.clone();
    const monthStarts = [];

    while (endPlusOne > interim || interim.format('M') === endPlusOne.format('M')) {
      monthStarts.push(interim.startOf('month').toDate());
      interim.add(1, 'month');
    }

    if (monthStarts.length === 1) {
      monthStarts.push(moment(monthStarts[0]).add(1, 'month').startOf('month').toDate());
    }
    return monthStarts;
  }

  private getLatestTarget(latestScan: any, targetAndHistoryType: UserTargetTypes) {
    const target = latestScan && latestScan.target;
    if (target && !target.reached) {
      const targetInfo: ITargetInfoViewModel = {
        date: target.date,
        targetMonthsNeeded: target.monthsNeeded,
        reached: target.reached,
      };

      switch (targetAndHistoryType) {
        case UserTargetTypes.Musclemass:
          targetInfo.value = target.muscleMass;
          break;
        case UserTargetTypes.Bodyweight:
          targetInfo.value = target.weight;
          break;
        case UserTargetTypes.Bodyfat:
          targetInfo.value = target.bodyFat;
          break;
      }

      return targetInfo;
    }

    return null;
  }

  private getLatestMilestone(latestScanWithTarget: any, targetAndHistoryType: UserTargetTypes) {
    const milestone = latestScanWithTarget && latestScanWithTarget.milestone;

    if (milestone) {
      const targetInfo: ITargetInfoViewModel = {
        date: new Date(milestone.date),
        reached: milestone.reached,
      };

      switch (targetAndHistoryType) {
        case UserTargetTypes.Musclemass:
          targetInfo.value = milestone.muscleMass;
          break;
        case UserTargetTypes.Bodyweight:
          targetInfo.value = milestone.weight;
          break;
        case UserTargetTypes.Bodyfat:
          targetInfo.value = milestone.bodyFat;
          break;
      }

      return targetInfo;
    }

    return null;
  }

  private calculateMilestoneFromTarget(
    fromDate: Date,
    targetInfo: any,
    userWeight: number,
    userTargetType: UserTargetTypes,
  ) {
    if (!targetInfo.dailyRateOfChange) {
      throw new Error(`No daily rate of change available on the target, cannot calculate milestone`);
    }

    const milestoneDate = moment(fromDate).add(2, 'weeks');
    const dailyRateOfChange = targetInfo.dailyRateOfChange;

    let mileStoneValue: number = 0;
    let milestone: any;

    if (userTargetType === UserTargetTypes.Musclemass) {
      mileStoneValue = (targetInfo.muscleMass - targetInfo.muscleMassChange) + (dailyRateOfChange * 14);

      milestone = mileStoneValue >= targetInfo.muscleMass
      ? { date: targetInfo.date, muscleMass: targetInfo.muscleMass }
      : { date: milestoneDate.toDate(), muscleMass: round(mileStoneValue, 10) };

    } else if (userTargetType === UserTargetTypes.Bodyweight) {
      mileStoneValue = userWeight - (dailyRateOfChange * 14);

      milestone = mileStoneValue <= targetInfo.weight
      ? { date: targetInfo.date, weight: targetInfo.weight }
      : { date: milestoneDate.toDate(), weight: round(mileStoneValue, 10) };

    } else if (userTargetType === UserTargetTypes.Bodyfat) {
      const currentBodyFatPercentage = targetInfo.bodyFat + Math.abs(targetInfo.bodyFatChange);
      const absoluteBodyFat = calculatebodyFatMassFromBodyFatPercentage(currentBodyFatPercentage, userWeight);
      const milestoneBodyFat = absoluteBodyFat - (dailyRateOfChange * 14);
      const milestoneBodyFatPercentage = calculatebodyFatPercentageFromBodyFatMass(milestoneBodyFat, userWeight);

      milestone = milestoneBodyFatPercentage <= targetInfo.bodyFat
      ? { date: targetInfo.date, bodyFat: targetInfo.bodyFat }
      : { date: milestoneDate.toDate(), bodyFat: round(milestoneBodyFatPercentage, 10) };
    }

    milestone = this.calculateMilestoneInTargetRange(targetInfo, milestone, userTargetType);

    return milestone;
  }

  private calculateMilestoneInTargetRange = (targetInfo: any, milestone: any, userTargetType: UserTargetTypes) => {
    const targetMoment = moment(targetInfo.date);
    const milestoneMoment = moment(milestone.date);
    const dayDiff = targetMoment.diff(milestoneMoment, 'days');

    if (dayDiff <= 7 || targetMoment < milestoneMoment) {
      switch (userTargetType) {
        case UserTargetTypes.Bodyfat:
          milestone = {
            ...milestone,
            date: targetInfo.date,
            bodyFat: targetInfo.bodyFat,
          };
          break;
        case UserTargetTypes.Bodyweight:
          milestone = {
            ...milestone,
            date: targetInfo.date,
            weight: targetInfo.weight,
          };
          break;
        case UserTargetTypes.Musclemass:
          milestone = {
            ...milestone,
            date: targetInfo.date,
            muscleMass: targetInfo.muscleMass,
          };
          break;
      }
    }

    return milestone;
  }

  private getGroupedWorkoutsInWeeks(
    workoutMinuteTotals: IWorkoutMinutes[],
    requestTime: Date,
    startOfWeek: Date,
    endOfWeek: Date,
    numberOfDaysToConsider: number,
  ) {
    const pastWorkouts = workoutMinuteTotals.filter((row) => {
      return row.endTime <= requestTime.getTime();
    });

    const groupedWorkoutsInWeeks = this.groupWorkoutsIntoWeeks(
      numberOfDaysToConsider,
      startOfWeek,
      endOfWeek,
      pastWorkouts,
    );

    return groupedWorkoutsInWeeks;
  }

  private getSundays(startDate: Date, endDate: Date, timezone: string) {
    const start = moment(startDate).tz(timezone);
    const end = moment(endDate).tz(timezone);
    const result = [];
    const current = start.clone();

    while (current.day(7).isSameOrBefore(end)) {
      result.push(current.clone());
    }

    return result;
  }

  private getPastAndFutureSundays(requestTime: Date, timezone: string, pastweeks: number, futureweeks: number) {
    const toDate = moment(requestTime).tz(timezone).endOf('day');
    const pastSundays =
      this.getSundays(toDate.clone().subtract(pastweeks, 'weeks').endOf('day').toDate(), toDate.toDate(), timezone);
    const futureSundays =
      this.getSundays(toDate.toDate(), toDate.clone().add(futureweeks, 'weeks').endOf('day').toDate(), timezone);
    return [...pastSundays, ...futureSundays];
  }

  private getFormatedSundays(
    pastAndFutureSundays: moment.Moment[], startWeek: number, week: number,
    requestTime: Date, timezone: string) {
    const sundays = graphWeeks
      .map((x) => x + week)
      .map((inx) => pastAndFutureSundays[inx]);
    const currentWeekIndex = graphWeeks
      .map((x) => x + week)
      .findIndex((inx) => moment(requestTime).tz(timezone).startOf('week').isSame(pastAndFutureSundays[inx], 'week'));
    return {
      startWeek,
      sundays,
      currentWeekIndex,
    };
  }

  private getWeeklyProgressSundays(groupedWorkoutsInWeeks: IWorkoutMinutes[][], requestTime: Date, timezone: string) {
    const sundays = this.getPastAndFutureSundays(requestTime, timezone, 5, 5);

    for (let week = 0; week < groupedWorkoutsInWeeks.length; week++) {
      const workoutMinutes = groupedWorkoutsInWeeks[week];
      const completedMinutes = this.sumDurationOfWorkoutInMinutes(workoutMinutes);
      const hasCompletedMinutes = completedMinutes > 0;
      if (hasCompletedMinutes) {
        return this.getFormatedSundays(sundays, week, week, requestTime, timezone);
      }
    }
    return this.getFormatedSundays(sundays, 2, 3, requestTime, timezone);
  }

  private getWeeklyProgressMinutes(groupedWorkoutsInWeeks: IWorkoutMinutes[][], startWeek: number) {
    const progressMinutes = [];

    for (let week = startWeek; week < groupedWorkoutsInWeeks.length; week++) {
      const workoutMinutes = groupedWorkoutsInWeeks[week];
      const completedMinutes = this.sumDurationOfWorkoutInMinutes(workoutMinutes);
      progressMinutes.push(completedMinutes);
    }

    return [
      ...progressMinutes,
      ...Array((graphWeeks.length - 1) - progressMinutes.length).fill(0),
    ];
  }

  private async getWeeklyProgressTargets(
    userProfile: UserProfile,
    groupedWorkoutsInWeeks: IWorkoutMinutes[][],
    positionOfLastWeeklyMinuteTargetChangeOnGraph: number,
    target: number,
    currentWeekIndex: number,
  ) {
    const targetBaselineMinutes = WorkoutTargetConfig[userProfile.longTermGoal] || 0;

    const previousTargetAboveBaseline =
      userProfile.weeklyWorkoutMinutesTarget &&
      userProfile.weeklyWorkoutMinutesTarget[
      userProfile.weeklyWorkoutMinutesTarget.length - 2
      ];

    const previousTarget = previousTargetAboveBaseline
      ? previousTargetAboveBaseline.targetAboveBaseline +
      targetBaselineMinutes
      : targetBaselineMinutes;

    const successfulWorkout: IWorkoutMinutes = {
      totalMinutes: target,
      startTime: 0,
      endTime: 0,
      workoutType: 0,
      load: 0,
    };

    const adjustedGroupedWorkoutsInWeeks = groupedWorkoutsInWeeks.fill([successfulWorkout],
      currentWeekIndex, groupedWorkoutsInWeeks.length);

    let index = positionOfLastWeeklyMinuteTargetChangeOnGraph === -1
      ? 0
      : positionOfLastWeeklyMinuteTargetChangeOnGraph;

    const previousWeeks = [
      ...Array(
        index,
      ).fill(previousTarget),
    ];

    while (index + workoutMinutesTargetConfig.success <= 5) {
      const groupedWorkoutsInWeeksToConsider = adjustedGroupedWorkoutsInWeeks
        .slice(index, index + workoutMinutesTargetConfig.success);

      const numberOfWeeklyTargetsReached =
        this.calculateNumberOfWeeklyTargetsReached(
          groupedWorkoutsInWeeksToConsider,
          target,
        );

      if (numberOfWeeklyTargetsReached === workoutMinutesTargetConfig.success) {
        const weeksTillGoalChangeWeeks = index + 3 - previousWeeks.length;

        const weeksWithLatestGoalChange = [
          ...Array(
            weeksTillGoalChangeWeeks,
          ).fill(target),
        ];

        const weeksAfterGoalChangeTillTargetIncrease = [
          ...Array(
            5 - weeksWithLatestGoalChange.length - previousWeeks.length,
          ).fill(target + workoutMinutesTargetConfig.increase),
        ];

        return [...previousWeeks, ...weeksWithLatestGoalChange, ...weeksAfterGoalChangeTillTargetIncrease];
      }

      index++;
    }

    const weeksAfterGoalChange = [
      ...Array(5 - previousWeeks.length).fill(target),
    ];

    return [...previousWeeks, ...weeksAfterGoalChange];
  }

  private groupWorkoutsIntoWeeks(
    numberOfDaysToConsider: number,
    startOfWeek: Date,
    endOfWeek: Date,
    pastWorkouts: IWorkoutMinutes[]): IWorkoutMinutes[][] {
    const groupedWorkoutsInWeeks = [];
    for (let daysToSubtract = numberOfDaysToConsider - 7; daysToSubtract >= 0; daysToSubtract = daysToSubtract - 7) {
      const startOfCurrentWeekGrouping = TimeSpan.getXDaysAgo(daysToSubtract, startOfWeek);
      const endOfCurrentWeekGrouping = TimeSpan.getXDaysAgo(daysToSubtract, endOfWeek);
      groupedWorkoutsInWeeks.push(pastWorkouts.filter((workout: IWorkoutMinutes) => {
        return workout.startTime >= startOfCurrentWeekGrouping.getTime() &&
          workout.endTime <= endOfCurrentWeekGrouping.getTime();
      }));
    }
    return groupedWorkoutsInWeeks;
  }

  private sumDurationOfWorkoutInMinutes(workouts: IWorkoutMinutes[]): number {
    return workouts.reduce((totalMinutes: number, workout: IWorkoutMinutes) => {
      const minutesForWorkout = getTotalMinutesCeil(workout.workoutType, workout.totalMinutes);
      return totalMinutes + minutesForWorkout;
    }, 0);
  }

  private getWorkoutTotals(workoutStatsForSixMonths: any) {
    if (!workoutStatsForSixMonths.selectedMonth.Total &&
      !workoutStatsForSixMonths.selectedMonth.Resistance &&
      !workoutStatsForSixMonths.selectedMonth.Cardio &&
      !workoutStatsForSixMonths.selectedMonth.Balance) {
      return { total: 0, build: 0, burn: 0, balance: 0 };
    }
    const total = workoutStatsForSixMonths.selectedMonth.Total || undefined;
    const build = workoutStatsForSixMonths.selectedMonth.Resistance || undefined;
    const burn = workoutStatsForSixMonths.selectedMonth.Cardio || undefined;
    const balance = workoutStatsForSixMonths.selectedMonth.Balance || undefined;
    return { total, build, burn, balance};
  }

  private getMuscleGroupPercentages(workoutStatsForSixMonths: any) {
    const backAndArmsCount = workoutStatsForSixMonths.selectedMonth[BigBodyGroup.BackAndArms] || 0;
    const upperAndArmsCount = workoutStatsForSixMonths.selectedMonth.Upper || 0;
    const lowerCount = workoutStatsForSixMonths.selectedMonth.Lower || 0;

    if (!backAndArmsCount && !upperAndArmsCount && !lowerCount) {
      return undefined;
    }

    const totals = backAndArmsCount + upperAndArmsCount + lowerCount;
    const backAndArms = Math.round((backAndArmsCount / totals) * 100);
    const upperAndArms = Math.round((upperAndArmsCount / totals) * 100);
    const lower = Math.round((lowerCount / totals) * 100);

    return { backAndArms, upperAndArms, lower };
  }

  private getWorkoutStats(workoutStatsForSixMonths: any, userProfile: UserProfile | undefined) {
    const { averageEffort, averageEffortChange } = this.getAverageEffortValues(workoutStatsForSixMonths);
    const { averageCalories, averageCaloriesChange } = this.getAverageCalorieValues(workoutStatsForSixMonths);
    const averageEffortGraphDataPoints =
      getWorkoutStatsGraphDataPoints(workoutStatsForSixMonths, WorkoutStat.Effort);
    const averageCaloriesGraphDataPoints =
      getWorkoutStatsGraphDataPoints(workoutStatsForSixMonths, WorkoutStat.Calories);
    const error = heartRateMonitorErrorHandler(userProfile, workoutStatsForSixMonths);

    if (!workoutStatsForSixMonths.selectedMonth.Total &&
      !averageEffortGraphDataPoints &&
      !averageCaloriesGraphDataPoints &&
      !error) {
      return undefined;
    }

    const { effortPointsRangeDegrees, effortPointsRangePercentages, effortPointsValuePercent, effortPointsRangeValues,
      calsRangeDegrees, calsRangePercentages, calsValuePercent, calsRangeValues } =
      this.getWorkoutStatRanges(averageEffort, averageCalories);

    const avWorkoutEffort = {
      description: 'Avg Workout Effort Points per Hour',
      valAndUnit: averageEffort ? `${round(averageEffort)}` : undefined,
      changeVal: averageEffortChange,
      changeUnit: '',
      changeDescription: changeDescription(averageEffortChange, 'month'),
      graphYAxis: this.calcWorkoutStatsGraphYAxis(workoutStatsForSixMonths, WorkoutStat.Effort),
      graphDataPoints: averageEffortGraphDataPoints,
      rangeDegrees: effortPointsRangeDegrees,
      rangePercentages: effortPointsRangePercentages,
      rangeValues: effortPointsRangeValues,
      valuePercent: effortPointsValuePercent,
      error,
    };
    const avWorkoutCal = {
      description: 'Avg Cals / Hour',
      valAndUnit: averageCalories ? `${round(averageCalories)}` : undefined,
      changeVal: averageCaloriesChange,
      changeUnit: '',
      changeDescription: changeDescription(averageCaloriesChange, 'month'),
      graphYAxis: this.calcWorkoutStatsGraphYAxis(workoutStatsForSixMonths, WorkoutStat.Calories),
      graphDataPoints: averageCaloriesGraphDataPoints,
      rangeDegrees: calsRangeDegrees,
      rangePercentages: calsRangePercentages,
      rangeValues: calsRangeValues,
      valuePercent: calsValuePercent,
      error,
    };

    return { avWorkoutEffort, avWorkoutCal };
  }

  private getScanStats(mostRecentScanStatsForSixMonths: any, userProfile: UserProfile | undefined) {
    const { currentWeight, weightChange } = this.getBodyWeightValues(mostRecentScanStatsForSixMonths);
    const muscleMassMeterChange = this.getMuscleMassMeterChange(mostRecentScanStatsForSixMonths, userProfile);
    const { currentPercentBodyFat, percentBodyFatChange } = this.getBodyFatValues(mostRecentScanStatsForSixMonths);

    const bodyWeightGraphDataPoints = getUserScanStatsGraphDataPoints(
      mostRecentScanStatsForSixMonths, ScanStat.Weight);

    const muscleMassMeterGraphDataPoints =
      getMuscleMassMeterGraphDataPoints(mostRecentScanStatsForSixMonths, userProfile);

    const fatGraphDataPoints = getUserScanStatsGraphDataPoints(
      mostRecentScanStatsForSixMonths, ScanStat.PercentBodyFat);

    const error = bodyscansErrorHandler(mostRecentScanStatsForSixMonths);

    if (!bodyWeightGraphDataPoints &&
      !muscleMassMeterGraphDataPoints &&
      !fatGraphDataPoints &&
      !error) {
      return undefined;
    }

    const { bodyFatRangeDegrees, bodyFatRangePercentages, bodyFatValuePercent, bodyFatStatRangeValues,
      muscleMassRangeDegrees, muscleMassRangePercentages, muscleMassValuePercent,
      muscleMassRangeValues, muscleMassMeter, muscleMassMeterKg } =
      this.getScanStatRanges(userProfile, currentWeight, currentPercentBodyFat);

    const { bodyFatRangeWeights } = this.getBodyWeightRanges(userProfile, currentWeight, currentPercentBodyFat);

    const mostRecentScanDate = this.getMostRecentScanDate(mostRecentScanStatsForSixMonths);
    const mostRecentScanType = this.getMostRecentScanType(mostRecentScanStatsForSixMonths);
    let scanDate;
    if (mostRecentScanDate) {
      scanDate = mostRecentScanDate.dateTime;
    }
    let totalBodyWeight: IStat = {
      description: 'Total Body Weight',
      changeVal: weightChange,
      changeUnit: 'kg',
      changeDescription: changeDescription(weightChange, 'scan'),
      graphYAxis: this.calcScanStatsGraphYAxis(mostRecentScanStatsForSixMonths, ScanStat.Weight),
      graphDataPoints: bodyWeightGraphDataPoints,
      type: mostRecentScanType,
      error,
    };
    if (currentWeight) {
      totalBodyWeight = {
        ...totalBodyWeight,
        valAndUnit: `${round(currentWeight)}kg`,
        rangeDegrees: bodyFatRangeDegrees,
        rangePercentages: bodyFatRangePercentages,
        rangeValues: bodyFatRangeWeights,
        valuePercent: bodyFatValuePercent,
      };
    }

    let muscleMass: IStat = {
      description: 'Muscle Mass',
      changeVal: muscleMassMeterChange,
      changeUnit: 'kg',
      changeDescription: changeDescription(muscleMassMeterChange, 'scan'),
      graphYAxis: getMinMaxGraphDataPoints(muscleMassMeterGraphDataPoints),
      graphDataPoints: muscleMassMeterGraphDataPoints,
      error,
    };
    if (muscleMassMeter) {
      muscleMass = {
        ...muscleMass,
        valAndUnit: `${muscleMassMeterKg}kg`,
        rangeDegrees: muscleMassMeter ? muscleMassRangeDegrees : undefined,
        rangePercentages: muscleMassRangePercentages,
        rangeValues: muscleMassRangeValues,
        valuePercent: muscleMassValuePercent && round(muscleMassValuePercent),
      };
    }

    let fat: IStat = {
      description: 'Fat %',
      changeVal: percentBodyFatChange,
      changeUnit: '%',
      changeDescription: changeDescription(percentBodyFatChange, 'scan'),
      graphYAxis: this.calcScanStatsGraphYAxis(mostRecentScanStatsForSixMonths, ScanStat.PercentBodyFat),
      graphDataPoints: fatGraphDataPoints,
      error,
    };
    if (currentPercentBodyFat) {
      fat = {
        ...fat,
        valAndUnit: `${round(currentPercentBodyFat)}%`,
        rangeDegrees: currentPercentBodyFat ? bodyFatRangeDegrees : undefined,
        rangePercentages: bodyFatRangePercentages,
        rangeValues: bodyFatStatRangeValues,
        valuePercent: bodyFatValuePercent,
      };
    }

    return { scanDate, totalBodyWeight, muscleMass, fat };
  }

  private getNutritionRatings(
    date: Date, mostRecentNutritionRatingsForSixMonths: any, nutritionRatingTimeline: any,
    lastNutritionRating: NutritionRating | undefined) {
    const dueDaysConfig = 1;
    const dueDay = moment(date).subtract(dueDaysConfig, 'day').toDate();
    const error = nutritionRatingsErrorHandler(
      nutritionRatingTimeline, lastNutritionRating, dueDay);
    if (!mostRecentNutritionRatingsForSixMonths) {
      return {
        error,
      };
    }

    const averageRating = nutritionRatingTimeline && nutritionRatingTimeline.totalRatingForMonth;
    const graphDataPoints = getNutritionRatingsGraphDataPoints(mostRecentNutritionRatingsForSixMonths);

    if (!graphDataPoints && !error) {
      return undefined;
    }

    return {
      description: 'Avg Rating this Month',
      avRating: averageRating,
      graphYAxis: { min: 0, max: 5, ticks: 5 },
      graphDataPoints,
      nutritionRatingTimeline,
      lastNutritionRating,
      error,
    };
  }

  private getWeeklyNutritionRatings(
    userProfile: UserProfile | undefined, requestTime: Date, timezone: string) {
    if (!userProfile) { return; }

    const { sundays: graphDateLabels, startWeek } =
      this.getWeeklyNutritionRatingSundays(userProfile, requestTime, timezone);
    const graphData = this.getNutritionRatingsWeeklyAverages(userProfile, startWeek, requestTime, timezone);
    const goal = 4.5;
    const formattedGraphDataLabels = graphDateLabels.map((gd) => gd.format('D MMM'));

    return {
      graphData,
      graphDateLabels: formattedGraphDataLabels,
      goal,
    };
  }

  private getWeeklyNutritionRatingSundays(userProfile: UserProfile, requestTime: Date, timezone: string) {
    const sundays = this.getPastAndFutureSundays(requestTime, timezone, 7, 7);

    for (let week = 1; week <= 6; week++) {
      const from = sundays[week].toDate();
      const to = sundays[week + 1].toDate();
      const hasHistory = NutritionRating.hasRatingForDateRange(userProfile.nutritionRatings, from, to);
      if (hasHistory) {
        return week === 6 ?
          this.getFormatedSundays(sundays, week - 2, week - 1, requestTime, timezone) :
          this.getFormatedSundays(sundays, week - 1, week, requestTime, timezone);
      }
    }
    return this.getFormatedSundays(sundays, 5, 6, requestTime, timezone);
  }

  private populateMissingGraphNutritionRatings(data: any[]) {
    let foundNumberFromEnd = false;
    return data.reverse().reduce((acc, cur) => {
      if (cur || (!cur && !foundNumberFromEnd)) {
        if (cur) {
          foundNumberFromEnd = true;
        }
        return [cur, ...acc];
      }
      foundNumberFromEnd = true;
      return [acc[0], ...acc];
    }, []);
  }

  private getNutritionRatingsWeeklyAverages(
    userProfile: UserProfile, startWeek: number, requestTime: Date, timezone: string) {
    const sundays = this.getPastAndFutureSundays(requestTime, timezone, 7, 7);

    const ratings = graphWeeks
      .map((week) => {
        const from = sundays[week + startWeek].toDate();
        const to = sundays[week + startWeek + 1].toDate();
        const ratingAverage = NutritionRating.calculateRatingAverage(userProfile.nutritionRatings, from, to);
        return ratingAverage && ratingAverage / nutitionRatingTransformDenominator;
      });
    return this.populateMissingGraphNutritionRatings(ratings);
  }

  private getAverageEffortValues(workoutStatsForSixMonths: any) {
    const averageEffort = (workoutStatsForSixMonths
      .selectedMonth.avWorkoutEffort / workoutStatsForSixMonths.selectedMonth.totalMinutes) * 60;
    const previousMonthAverageEffort = (workoutStatsForSixMonths
      .previousMonth.avWorkoutEffort / workoutStatsForSixMonths.previousMonth.totalMinutes) * 60;
    const averageEffortChange = calcChangeDiff(averageEffort, previousMonthAverageEffort) || undefined;
    return { averageEffort, averageEffortChange };
  }

  private getAverageCalorieValues(workoutStatsForSixMonths: any) {
    const averageCalories = (workoutStatsForSixMonths.selectedMonth.Calories /
      workoutStatsForSixMonths.selectedMonth.totalMinutes) * 60;
    const previousMonthAverageCalories = (workoutStatsForSixMonths
      .previousMonth.Calories / workoutStatsForSixMonths.previousMonth.totalMinutes) * 60;
    const averageCaloriesChange = calcChangeDiff(averageCalories, previousMonthAverageCalories) || undefined;
    return { averageCalories, averageCaloriesChange };
  }

  private getMostRecentScanStats(mostRecentScanStatsForSixMonths: any) {
    const keys = Object.keys(mostRecentScanStatsForSixMonths);
    const mostRecentScanStats = keys.find((key) => {
      const value = mostRecentScanStatsForSixMonths[key];
      return value.hasOwnProperty('analysis');
    });
    if (mostRecentScanStats) {
      return mostRecentScanStatsForSixMonths[mostRecentScanStats];
    }
    return null; // There is no scans for six months
  }

  private async getScanStatsForMonthOrLatest(uid: any, date: Date, timezone: string | undefined) {
    let scanDate;
    let requiresCurrentMonthBodyScan;
    let currentWeight;
    let currentPercentBodyFat;
    let currentMuscleMass;
    let scanType;
    const scanStatsForMonth = await this.getUserScanStatsForMonth(uid, date, timezone);
    if (scanStatsForMonth && scanStatsForMonth.date) {
      scanDate = scanStatsForMonth.date;
      requiresCurrentMonthBodyScan = false;
      currentWeight = scanStatsForMonth.analysis && scanStatsForMonth.analysis.weight.currentValue;
      currentPercentBodyFat = scanStatsForMonth.analysis && scanStatsForMonth.analysis.percentBodyFat.currentValue;
      currentMuscleMass = scanStatsForMonth.analysis && scanStatsForMonth.analysis.skeletalMuscleMass.currentValue;
      scanType = scanStatsForMonth.type;
    } else {
      const scanStatsRange = await this.getUserScanStatsForMonthRange(uid, date, timezone);
      requiresCurrentMonthBodyScan = true;
      if (scanStatsRange && scanStatsRange.length !== 0) {
        const latestScan = scanStatsRange.slice(-1)[0];
        scanDate = latestScan.date;
        currentWeight = latestScan.analysis && latestScan.analysis.weight.currentValue;
        currentPercentBodyFat = latestScan.analysis && latestScan.analysis.percentBodyFat.currentValue;
        currentMuscleMass = latestScan.analysis && latestScan.analysis.skeletalMuscleMass.currentValue;
        scanType = 0;
      }
    }
    const error = scanStatsErrorHandler(scanStatsForMonth);

    return {
      scanDate,
      requiresCurrentMonthBodyScan,
      currentWeight,
      currentPercentBodyFat,
      currentMuscleMass,
      error,
      scanType,
    };
  }

  private getMostRecentScanDate(mostRecentScanStatsForSixMonths: any) {
    if (!mostRecentScanStatsForSixMonths) {
      return undefined;
    }
    const mostRecentScanStats = this.getMostRecentScanStats(mostRecentScanStatsForSixMonths);

    if (mostRecentScanStats) {
      return {
        dateOnly: mostRecentScanStats.date,
        dateTime: mostRecentScanStats.dateTime,
      };
    }
    return undefined;
  }

  private getMostRecentScanType(mostRecentScanStatsForSixMonths: any): BodyScanTypes {
    if (!mostRecentScanStatsForSixMonths) {
      return 0;
    }
    const mostRecentScanStats = this.getMostRecentScanStats(mostRecentScanStatsForSixMonths);

    if (mostRecentScanStats) {
      return mostRecentScanStats.type;
    }
    return 0;
  }

  private getBodyWeightValues(mostRecentScanStatsForSixMonths: any) {
    let currentWeight;
    let weightChange;
    if (mostRecentScanStatsForSixMonths) {
      currentWeight =
        getAnalysisValue(mostRecentScanStatsForSixMonths, StatMonth.SelectedMonth, ScanStat.Weight);
      if (!currentWeight) {
        const mostRecentScanStats = this.getMostRecentScanStats(mostRecentScanStatsForSixMonths);
        currentWeight = mostRecentScanStats ?
          mostRecentScanStats.analysis.weight.currentValue : undefined;
      }
      const previousWeight =
        getAnalysisValue(mostRecentScanStatsForSixMonths, StatMonth.PreviousMonth, ScanStat.Weight);
      weightChange = calcChangeDiff(currentWeight, previousWeight) || undefined;
    }
    return { currentWeight, weightChange };
  }

  private getBodyFatValues(mostRecentScanStatsForSixMonths: any) {
    let currentPercentBodyFat;
    let percentBodyFatChange;

    if (mostRecentScanStatsForSixMonths) {
      currentPercentBodyFat =
        getAnalysisValue(mostRecentScanStatsForSixMonths, StatMonth.SelectedMonth, ScanStat.PercentBodyFat);
      if (!currentPercentBodyFat) {
        const mostRecentScanStats = this.getMostRecentScanStats(mostRecentScanStatsForSixMonths);
        currentPercentBodyFat = mostRecentScanStats ?
          mostRecentScanStats.analysis.percentBodyFat.currentValue : undefined;
      }
      const previousPercentBodyFat =
        getAnalysisValue(mostRecentScanStatsForSixMonths, StatMonth.PreviousMonth, ScanStat.PercentBodyFat);
      percentBodyFatChange = calcChangeDiff(currentPercentBodyFat, previousPercentBodyFat) || undefined;
    }
    return { currentPercentBodyFat, percentBodyFatChange };
  }

  private getMuscleMassMeterChange(mostRecentScanStatsForSixMonths: any, userProfile: UserProfile | undefined) {
    let percentChange;

    if (mostRecentScanStatsForSixMonths && userProfile) {
      let currentWeight =
        getAnalysisValue(mostRecentScanStatsForSixMonths, StatMonth.SelectedMonth, ScanStat.Weight);
      if (!currentWeight) {
        const mostRecentScanStats = this.getMostRecentScanStats(mostRecentScanStatsForSixMonths);
        currentWeight = mostRecentScanStats ?
          mostRecentScanStats.analysis.weight.currentValue : undefined;
      }
      const previousWeight =
        getAnalysisValue(mostRecentScanStatsForSixMonths, StatMonth.PreviousMonth, ScanStat.Weight);

      let currentPercentBodyFat =
        getAnalysisValue(mostRecentScanStatsForSixMonths, StatMonth.SelectedMonth, ScanStat.PercentBodyFat);
      if (!currentPercentBodyFat) {
        const mostRecentScanStats = this.getMostRecentScanStats(mostRecentScanStatsForSixMonths);
        currentPercentBodyFat = mostRecentScanStats ?
          mostRecentScanStats.analysis.percentBodyFat.currentValue : undefined;
      }
      const previousPercentBodyFat =
        getAnalysisValue(mostRecentScanStatsForSixMonths, StatMonth.PreviousMonth, ScanStat.PercentBodyFat);

      const currentMuscleMassMeter = calcMuscleMassMeter(userProfile, currentWeight, currentPercentBodyFat);
      const previousMuscleMassMeter = calcMuscleMassMeter(userProfile, previousWeight, previousPercentBodyFat);
      const currentMuscleMassMeterKg = calcMuscleMassMeterKg(userProfile, currentMuscleMassMeter);
      const previousMuscleMassMeterKg = calcMuscleMassMeterKg(userProfile, previousMuscleMassMeter);

      percentChange = calcChangeDiff(currentMuscleMassMeterKg, previousMuscleMassMeterKg) || undefined;
    }
    return percentChange;
  }

  private calcWorkoutStatsGraphYAxis(workoutStatsForSixMonths: any, statType: WorkoutStat) {
    if (!workoutStatsForSixMonths) {
      return undefined;
    }

    let values: any = [];

    for (const key in workoutStatsForSixMonths) {
      if (workoutStatsForSixMonths.hasOwnProperty(key)) {
        const element = workoutStatsForSixMonths[key];
        if (element && element[statType]) {
          values = [...values, (element[statType] / element.totalMinutes) * 60];
        }
      }
    }

    if (!values || values.length === 0) {
      return undefined;
    }

    let min = Math.min(...values);
    let max = Math.max(...values);

    // buffers
    min -= 30;
    max += 10;

    return { min: round(min), max: round(max), ticks: 4 };
  }

  private calcScanStatsGraphYAxis(mostRecentScanStatsForSixMonths: any, statType: ScanStat) {
    if (!mostRecentScanStatsForSixMonths) {
      return undefined;
    }

    let values: any = [];

    for (const key in mostRecentScanStatsForSixMonths) {
      if (mostRecentScanStatsForSixMonths.hasOwnProperty(key)) {
        const element = mostRecentScanStatsForSixMonths[key];
        if (element.analysis && element.analysis[statType]) {
          values = [...values, element.analysis[statType].currentValue];
        }
      }
    }

    if (!values || values.length === 0) {
      return undefined;
    }

    let min = Math.min(...values);
    const max = Math.max(...values);

    if (statType === 'weight') {
      min -= 4;
    } else {
      min -= 1;
    }

    return { min, max, ticks: 5 };
  }

  private getYearAndMonthDiff(selectedYear: number, selectedMonth: number, monthDiff: number) {
    const selectedDate = new Date(selectedYear, selectedMonth - 1, 1);
    const dateDiff = new Date(selectedDate.setMonth(selectedDate.getMonth() - monthDiff));
    const year = dateDiff.getFullYear();
    const month = dateDiff.getMonth() + 1;

    return { year, month };
  }

  private getStatsForMonthdiff(workoutStatsRangeFromView: any,
                               initialYear: number, initialMonth: number, monthDiff?: number) {
    const calendarMonth = monthDiff ?
      this.getYearAndMonthDiff(initialYear, initialMonth, monthDiff) : { year: initialYear, month: initialMonth };

    const statsForCalendarMonth = workoutStatsRangeFromView
      .filter((x: any) => x.year === calendarMonth.year && x.month === calendarMonth.month);
    return { ...calendarMonth, ...statsForCalendarMonth[0] };
  }

  private async getUserBodyScansForRange(uid: string, date: Date, timezone: string | undefined, historicMonths: number)
    : Promise<IBodyScanStat[] | undefined> {
    let toDate = moment(date).endOf('day');
    let fromDate = toDate.clone().subtract(historicMonths, 'months').endOf('day');

    if (timezone !== undefined) {
      toDate = moment(date).tz(timezone).endOf('day');
      fromDate = toDate.clone().subtract(historicMonths, 'months').endOf('day');
    }

    const scanStatsForuser = await
      this.bodyScanRepository.getByUidAndDateRange(uid, fromDate.toDate(), toDate.toDate());
    if (!scanStatsForuser) { return; }

    const validScanStats = scanStatsForuser.filter((scan) => !scan.invalid);
    const groupedByScanDate = groupBy(validScanStats, 'dateOfScanDateOnly');
    const userScanStats = takeFirstOnGroup(groupedByScanDate);

    return userScanStats
      .sort((scanA, scanB) => scanA.dateOfScanDateOnly.getTime() - scanB.dateOfScanDateOnly.getTime())
      .map(this.mapStats);
  }

  private mapStats(scan: any) {
    return {
      date: scan.dateOfScanDateOnly,
      dateTime: scan.dateOfScan,
      year: scan.dateOfScanDateOnly.getFullYear(),
      month: scan.dateOfScanDateOnly.getMonth() + 1,
      ['year-month']: `${scan.dateOfScanDateOnly.getFullYear()}-${scan.dateOfScanDateOnly.getMonth() + 1}`,
      analysis: scan.analysis,
      type: scan.type,
      target: scan.target,
      milestone: scan.milestone,
    };
  }

  private async getUserScanStatsForMonthRange(uid: string, selectedDate: Date, timezone: string | undefined) {
    const userScanStatsRange = await this.getUserBodyScansForRange(uid, selectedDate, timezone, 5);
    const groupedUserScanStatsRange = groupBy(userScanStatsRange, 'year-month');
    const keys = Object.keys(groupedUserScanStatsRange);
    return keys.map((key) => {
      const value = groupedUserScanStatsRange[key];
      return Object.assign({}, ...value);
    });
  }

  private async getWorkoutStatsForMonthRange(
    uid: string, initialYear: number, initialMonth: number) {
    const calendarMonthEnd = this.getYearAndMonthDiff(initialYear, initialMonth, 5);
    const workoutStatsRangeFromView = await this.userWorkoutStatsRepository.getUserWorkoutStats
      (uid, calendarMonthEnd.year, calendarMonthEnd.month, initialYear, initialMonth);

    const workoutStatsRange = workoutStatsRangeFromView.map((x) => {
      return {
        year: x.key[1],
        month: x.key[2],
        ['year-month']: `${x.key[1]}-${x.key[2]}`,
        [x.key[3]]: x.value,
      };
    });

    const groupedWorkoutStatsRange = groupBy(workoutStatsRange, 'year-month');
    const keys = Object.keys(groupedWorkoutStatsRange);
    return keys.map((key) => {
      const value = groupedWorkoutStatsRange[key];
      return Object.assign({}, ...value);
    });
  }

  private async getWorkoutStatsForSixMonths(uid: string, year: number, month: number) {
    const statsRange = await this.getWorkoutStatsForMonthRange(uid, year, month);

    return {
      selectedMonth: this.getStatsForMonthdiff(statsRange, year, month),
      previousMonth: this.getStatsForMonthdiff(statsRange, year, month, 1),
      monthThree: this.getStatsForMonthdiff(statsRange, year, month, 2),
      monthFour: this.getStatsForMonthdiff(statsRange, year, month, 3),
      monthFive: this.getStatsForMonthdiff(statsRange, year, month, 4),
      monthSix: this.getStatsForMonthdiff(statsRange, year, month, 5),
    };
  }

  private async getWorkoutStatsForLastweek(uid: string, dateUtc: Date, timezone: string | undefined) {
    let toDate = moment(dateUtc).endOf('day');
    let fromDate = toDate.clone().subtract(7, 'days').endOf('day');

    if (timezone !== undefined) {
      toDate = moment(dateUtc).tz(timezone).endOf('day');
      fromDate = toDate.clone().subtract(7, 'days').endOf('day');
    }

    return await this.workoutRepository.getHistoryBetweenDates(uid, fromDate.toDate(), toDate.toDate());
  }

  private getWorkoutCountStatRanges(workoutCount: number) {
    const workoutRangeDegrees = StatRangesConfig.calcWorkoutRangeDegrees();
    const workoutRangePercentages = StatRangesConfig.calcWorkoutRangePercentages();
    const workoutValuePercent = StatRangesConfig.calcWorkoutGaugePercent(workoutCount);
    return {
      workoutRangeDegrees,
      workoutRangePercentages,
      workoutValuePercent,
    };
  }

  private getWorkoutStatRanges(averageEffort: number, averageCalories: number) {
    const effortPointsRangeDegrees = StatRangesConfig.calcEffortPointsRangeDegrees();
    const effortPointsRangePercentages = StatRangesConfig.calcEffortPointsRangePercentages();
    const effortPointsValuePercent = StatRangesConfig.calcEffortPointsGaugePercent(averageEffort);
    const effortPointsRangeValues = StatRangesConfig.getCalcEffortPointsRanges();
    const calsRangeDegrees = StatRangesConfig.calcCaloriesRangeDegrees();
    const calsRangePercentages = StatRangesConfig.calcCaloriesRangePercentages();
    const calsValuePercent = StatRangesConfig.calcCaloriesGaugePercent(averageCalories);
    const calsRangeValues = StatRangesConfig.getCaloriesRanges();

    return {
      effortPointsRangeDegrees,
      effortPointsRangePercentages,
      effortPointsValuePercent,
      effortPointsRangeValues,
      calsRangeDegrees,
      calsRangePercentages,
      calsValuePercent,
      calsRangeValues,
    };
  }

  private getScanStatRanges(userProfile: UserProfile | undefined, weight: number, bodyFat: number) {
    let bodyFatRangeDegrees;
    let bodyFatRangePercentages;
    let bodyFatValuePercent;
    let bodyFatStatRangeValues;
    let muscleMassRangeDegrees;
    let muscleMassRangePercentages;
    let muscleMassValuePercent;
    let muscleMassRangeValues;
    let muscleMassMeter;
    let muscleMassMeterKg;

    if (userProfile) {
      const bodyFatStatRanges = this.getBodyFatStatRanges(userProfile, bodyFat);
      bodyFatRangeDegrees = bodyFatStatRanges.bodyFatRangeDegrees;
      bodyFatRangePercentages = bodyFatStatRanges.bodyFatRangePercentages;
      bodyFatValuePercent = bodyFatStatRanges.bodyFatValuePercent;
      bodyFatStatRangeValues = bodyFatStatRanges.bodyFatStatRangeValues;

      const muscleMassStatRanges = this.getMuscleMassStatRanges(userProfile, weight, bodyFat);
      muscleMassRangeDegrees = muscleMassStatRanges.muscleMassRangeDegrees;
      muscleMassRangePercentages = muscleMassStatRanges.muscleMassRangePercentages;
      muscleMassValuePercent = muscleMassStatRanges.muscleMassValuePercent;
      muscleMassRangeValues = muscleMassStatRanges.muscleMassRangeValues;
      muscleMassMeter = muscleMassStatRanges.muscleMassMeter;
      muscleMassMeterKg = muscleMassStatRanges.muscleMassMeterKg;
    }

    return {
      bodyFatValuePercent, bodyFatRangePercentages, bodyFatRangeDegrees, bodyFatStatRangeValues,
      muscleMassRangePercentages, muscleMassValuePercent, muscleMassRangeDegrees,
      muscleMassRangeValues, muscleMassMeter, muscleMassMeterKg,
    };
  }

  private getBodyFatStatRanges(userProfile: UserProfile, bodyFat: number) {
    const bodyFatStatRange = StatRangesConfig.getBodyFatStatRangeForUser(
      userProfile.gender, userProfile.age, userProfile.longTermGoal);
    const bodyFatValuePercent = StatRangesConfig.calcBodyFatGaugePercent(bodyFatStatRange, bodyFat);
    const bodyFatRangePercentages = StatRangesConfig.calcBodyFatRangePercentages(bodyFatStatRange);
    const bodyFatRangeDegrees = StatRangesConfig.calcBodyFatRangeDegrees(bodyFatStatRange);
    const bodyFatStatRangeValues = StatRangesConfig.getBodyFatRanges(bodyFatStatRange);

    return { bodyFatValuePercent, bodyFatRangePercentages, bodyFatRangeDegrees, bodyFatStatRangeValues };
  }

  private getMuscleMassStatRanges(userProfile: UserProfile, weight: number, bodyFat: number) {
    const muscleMassMeter = calcMuscleMassMeter(userProfile, weight, bodyFat);
    const muscleMassMeterKg = calcMuscleMassMeterKg(userProfile, muscleMassMeter);
    const muscleMassStatRange = StatRangesConfig.getMuscleMassStatRangeForUser(
      userProfile.gender, userProfile.longTermGoal);
    const muscleMassRangePercentages = StatRangesConfig.calcMuscleMassRangePercentages(muscleMassStatRange);
    const muscleMassValuePercent = StatRangesConfig.calcMuscleMassGaugePercent(muscleMassStatRange, muscleMassMeter);
    const muscleMassRangeDegrees = StatRangesConfig.calcMuscleMassRangeDegrees(muscleMassStatRange);
    const muscleMassRangeValues = StatRangesConfig.getMuscleMassKgRanges(muscleMassStatRange, userProfile.height);

    return {
      muscleMassRangePercentages, muscleMassValuePercent,
      muscleMassRangeDegrees, muscleMassRangeValues,
      muscleMassMeter: round(muscleMassMeter),
      muscleMassMeterKg: round(muscleMassMeterKg),
    };
  }

  private getBodyWeightRanges(userProfile: UserProfile | undefined, weight: number, bodyFat: number) {
    let bodyFatRangePercentages;
    let bodyFatRangeWeights;
    let bodyWeightValuePercent;

    if (userProfile) {
      const fatlessWeight = weight * (1 - (bodyFat / 100));
      const bodyFatStatRange = StatRangesConfig.getBodyFatStatRangeForUser(
        userProfile.gender, userProfile.age, userProfile.longTermGoal);
      bodyFatRangePercentages = StatRangesConfig.calcBodyFatRangePercentages(bodyFatStatRange);
      bodyFatRangeWeights = StatRangesConfig.calcBodyWeightRanges(bodyFatStatRange, weight, fatlessWeight);
      bodyWeightValuePercent = StatRangesConfig.calcBodyWeightGaugePercent(bodyFatStatRange, weight, fatlessWeight);
    }

    return { bodyFatRangePercentages, bodyFatRangeWeights, bodyWeightValuePercent };
  }

  private async getUserScanStatsForSixMonths(uid: string, date: Date, timezone: string | undefined) {
    const scanStatsRange = await this.getUserScanStatsForMonthRange(uid, date, timezone);
    if (!scanStatsRange || scanStatsRange.length === 0) { return; }

    const momentDate = moment(date);
    let year = momentDate.year();
    let month = momentDate.month() + 1;
    if (timezone !== undefined) {
      year = momentDate.tz(timezone).year();
      month = momentDate.tz(timezone).month() + 1;
    }

    return {
      selectedMonth: this.getStatsForMonthdiff(scanStatsRange, year, month),
      previousMonth: this.getStatsForMonthdiff(scanStatsRange, year, month, 1),
      monthThree: this.getStatsForMonthdiff(scanStatsRange, year, month, 2),
      monthFour: this.getStatsForMonthdiff(scanStatsRange, year, month, 3),
      monthFive: this.getStatsForMonthdiff(scanStatsRange, year, month, 4),
      monthSix: this.getStatsForMonthdiff(scanStatsRange, year, month, 5),
    };
  }

  private async getUserScanStatsForMonth(uid: any, date: Date, timezone: string | undefined) {
    const scanStatsRange = await this.getUserScanStatsForMonthRange(uid, date, timezone);
    if (!scanStatsRange || scanStatsRange.length === 0) { return; }

    const momentDate = moment(date);
    let year = momentDate.year();
    let month = momentDate.month() + 1;
    if (timezone !== undefined) {
      year = momentDate.tz(timezone).year();
      month = momentDate.tz(timezone).month() + 1;
    }

    return this.getStatsForMonthdiff(scanStatsRange, year, month);
  }

  private getNutritionRatingForMonth(
    userProfile: UserProfile, initialYear: any, initialMonth: any, monthDiff: any, initialDay?: any) {
    const calendarMonth = monthDiff ?
      this.getYearAndMonthDiff(initialYear, initialMonth, monthDiff) : { year: initialYear, month: initialMonth };
    let date;
    if (!initialDay) {
      const day = getDaysInMonth(new Date(calendarMonth.year, calendarMonth.month - 1, 1));
      date = moment([calendarMonth.year, calendarMonth.month - 1, day]).endOf('day').toDate();
    } else {
      date = moment([calendarMonth.year, calendarMonth.month - 1, initialDay]).endOf('day').toDate();
    }

    const rangeForMonth = this.getNutritionRatingTimeline(userProfile, date);
    return {
      year: calendarMonth.year,
      month: calendarMonth.month,
      date,
      rating: rangeForMonth && rangeForMonth.totalRatingForMonth,
    };
  }

  private async getNutritionRatingsForSixMonths(userProfile: UserProfile | undefined, date: Date) {
    if (!userProfile) { return; }

    const year = date.getUTCFullYear();
    const month = date.getMonth() + 1;

    return {
      selectedMonth: this.getNutritionRatingForMonth(userProfile, year, month, null, date.getDate()),
      previousMonth: this.getNutritionRatingForMonth(userProfile, year, month, 1),
      monthThree: this.getNutritionRatingForMonth(userProfile, year, month, 2),
      monthFour: this.getNutritionRatingForMonth(userProfile, year, month, 3),
      monthFive: this.getNutritionRatingForMonth(userProfile, year, month, 4),
      monthSix: this.getNutritionRatingForMonth(userProfile, year, month, 5),
    };
  }

  private getLastNutritionRating(userProfile: UserProfile | undefined) {
    if (!userProfile) { return; }
    return NutritionRating.getLastRating(userProfile.nutritionRatings);
  }

  private getNextRating(ratingsForSixMonths: NutritionRating[], date: Date) {
    const nextRating = ratingsForSixMonths
      .sort((a, b) => (a.date.getTime() - b.date.getTime()))
      .find((x) => date.getTime() - x.date.getTime() <= 0);
    if (nextRating) {
      return nextRating.percent;
    }
    return null;
  }

  private populateDailyRatings(ratingsForSixMonths: NutritionRating[], toDate: Date): any[] {
    const year = toDate.getFullYear();
    const month = toDate.getMonth() + 1;
    const daysToUse = toDate.getDate();
    const daylyRatings = [];

    for (let i = daysToUse; i > 1 - 1; i--) {
      let rating = null;
      const dateString = `${year}-${('0' + month).slice(-2)}-${('0' + i).slice(-2)}`;
      const dateObj = new Date(dateString);

      const ratingForThisDate = ratingsForSixMonths.find((x) => x.date.toISOString().includes(dateString));
      rating = ratingForThisDate ? ratingForThisDate.percent : this.getNextRating(ratingsForSixMonths, dateObj);

      daylyRatings.push({
        date: dateObj,
        percent: rating,
      });
    }
    return daylyRatings.reverse();
  }

  private timeLineMappedFromGroupSequence(groupedSequence: NutritionRating[][]) {
    const totalDaysInGroupedSequence = [].concat.apply([], groupedSequence).length;
    return groupedSequence.map((item, inx) => {
      const nullDays = item.filter((x) => x.percent === null).length;
      const activeDays = totalDaysInGroupedSequence - nullDays;
      let days = 1;
      const from = item[0].date;
      let to = item[0].date;
      if (item.length > 1) {
        to = item[item.length - 1].date;
        days = item.length;
        const next = groupedSequence[inx + 1];
        if (next) {
          const nextFrom = next[0].date;
          const daysDiff = moment.utc(nextFrom).diff(moment.utc(from), 'days');
          days = daysDiff;
        }
      }
      const ratingPers = item[0].percent;
      const rating = ratingPers / nutitionRatingTransformDenominator;
      const percentOfActiveDays = activeDays !== 0 ? days / activeDays : 0;
      const ratingForSeq = rating * percentOfActiveDays;

      return {
        from,
        to,
        rating,
        percentOfActiveDays,
        ratingForSeq,
      };
    });
  }

  private getNutritionRatingTimeline(userProfile: UserProfile | undefined, requestDate: Date) {
    if (!userProfile) { return; }

    const toDate = requestDate;
    const fromDate = new Date();
    fromDate.setFullYear(toDate.getFullYear());
    fromDate.setMonth(toDate.getMonth() - 5);
    fromDate.setDate(1);
    fromDate.setUTCHours(0, 0, 0, 0);

    const ratings = NutritionRating.getRatingsInDateRange(userProfile.nutritionRatings, fromDate, toDate);
    if (!ratings) { return; }

    const daylyRatingsForRequestedMonth = this.populateDailyRatings(ratings, requestDate);
    const groupedSequence = groupedSequenceFromRatingsForRequestedMonth(daylyRatingsForRequestedMonth);
    const timeline = this.timeLineMappedFromGroupSequence(groupedSequence);
    const totalRatingForMonth = timeline.reduce((sum, item) => sum + item.ratingForSeq, 0);

    return {
      timeline,
      totalRatingForMonth: Math.round(totalRatingForMonth * 10) / 10,
    };
  }
  private calculateWeeklyWorkoutMinutesTarget(
    groupedWorkoutsInWeeks: IWorkoutMinutes[][],
    targetBaselineMinutes: number,
    requestTime: Date,
    startDate: Date,
    timezone: string,
    user: UserProfile,
  ) {
    const mondayAfterLastCycle = new Date(TimeSpan.getNextMonday(startDate));
    const lastWeeklyMinutesChange: IWeeklyWorkoutMinutesTarget = this.getLatestWeeklyTargetMinutesChange(user);
    const dateOfLastWeeklyMinutesChange = new Date(lastWeeklyMinutesChange.dateOfTargetChange);

    const isEligibleToChange =
      dateOfLastWeeklyMinutesChange < mondayAfterLastCycle;

    if (!isEligibleToChange) {
      return lastWeeklyMinutesChange.targetAboveBaseline + targetBaselineMinutes;
    }

    const weeklyWorkoutMinutesChange = this.calculateWeeklyWorkoutMinutesTargetChange(
      groupedWorkoutsInWeeks,
      targetBaselineMinutes,
      lastWeeklyMinutesChange.targetAboveBaseline,
    );
    this.updateUserWeeklyMinutesTarget(
      weeklyWorkoutMinutesChange,
      lastWeeklyMinutesChange.targetAboveBaseline,
      requestTime,
      timezone,
      user,
    );

    return targetBaselineMinutes + weeklyWorkoutMinutesChange;
  }

  private calculateWeeklyWorkoutMinutesTargetChange(
    groupedWorkoutsInWeeks: IWorkoutMinutes[][],
    targetBaselineMinutes: number,
    targetMinutes: number,
  ) {
    const targetChangeWithBaseLine = targetBaselineMinutes + targetMinutes;
    const groupedWorkoutsExcludingCurrentWeek = groupedWorkoutsInWeeks.filter(
      (element, index) => index < groupedWorkoutsInWeeks.length - 1,
    );

    const numberOfWeeklyTargetsReached =
      this.calculateNumberOfWeeklyTargetsReached(
        groupedWorkoutsExcludingCurrentWeek,
        targetChangeWithBaseLine,
      );
    let newWeeklyTargetMinutes = targetMinutes;

    if (numberOfWeeklyTargetsReached === workoutMinutesTargetConfig.success) {
      newWeeklyTargetMinutes = targetChangeWithBaseLine + workoutMinutesTargetConfig.increase;

      return newWeeklyTargetMinutes >= workoutMinutesTargetConfig.max
        ? workoutMinutesTargetConfig.max - targetBaselineMinutes
        : targetMinutes + workoutMinutesTargetConfig.increase;
    }

    if (numberOfWeeklyTargetsReached === workoutMinutesTargetConfig.fail) {
      newWeeklyTargetMinutes = targetChangeWithBaseLine + workoutMinutesTargetConfig.decrease;

      return newWeeklyTargetMinutes <= targetBaselineMinutes ? 0 : targetMinutes + workoutMinutesTargetConfig.decrease;
    }

    return newWeeklyTargetMinutes;
  }

  private getLatestWeeklyTargetMinutesChange(user: UserProfile) {
    const userWeeklyTarget = user.weeklyWorkoutMinutesTarget &&
      user.weeklyWorkoutMinutesTarget[user.weeklyWorkoutMinutesTarget.length - 1];
    return userWeeklyTarget || { targetAboveBaseline: 0, dateOfTargetChange: new Date(0).toString() };
  }

  private calculateNumberOfWeeklyTargetsReached(
    groupedWorkoutsInWeeks: IWorkoutMinutes[][],
    currentTargetMinutes: number,
  ) {
    const weeklyWorkoutMinuteTargetsReached = groupedWorkoutsInWeeks.reduce(
      (
        targetsReached: number,
        weeklyWorkoutMinutes: IWorkoutMinutes[],
      ) => {
        const totalWorkoutMinutes = this.sumDurationOfWorkoutInMinutes(weeklyWorkoutMinutes);

        if (totalWorkoutMinutes >= currentTargetMinutes) {
          targetsReached++;
        }

        return targetsReached;
      }, 0);

    return weeklyWorkoutMinuteTargetsReached;
  }

  private updateUserWeeklyMinutesTarget(
    weeklyWorkoutMinutesChange: number,
    currentTargetMinutes: number,
    requestTime: Date,
    timezone: string,
    user: UserProfile,
  ) {
    if (weeklyWorkoutMinutesChange !== currentTargetMinutes) {
      const lastWeek = TimeSpan.getAWeekAgo(requestTime);
      const endOfLastWeek = TimeSpan.getEndOfISOWeekInTimezone(lastWeek, timezone).toISOString();
      const weeklyWorkoutMinutesTarget: IWeeklyWorkoutMinutesTarget = {
        dateOfTargetChange: endOfLastWeek,
        targetAboveBaseline: weeklyWorkoutMinutesChange,
      };

      if (user.weeklyWorkoutMinutesTarget) {
        user.weeklyWorkoutMinutesTarget = [...user.weeklyWorkoutMinutesTarget, weeklyWorkoutMinutesTarget];
      } else {
        user.weeklyWorkoutMinutesTarget = [weeklyWorkoutMinutesTarget];
      }

      // TODO: should this be updating in the background?
      this.userProfileRepository.update(user)
        .catch((e) => this.logger.warn('updateUserWeeklyMinutesTarget failed', e));
    }
    return user;
  }

  private getPositionOfLastWeeklyMinuteTargetChangeOnGraph(
    userProfile: UserProfile,
    graphDateLabels: any[],
    timezone: string,
  ) {
    let positionOnGraphOfLastTargetChange = -1;
    const latestWeeklyTargetMinutesChange = this.getLatestWeeklyTargetMinutesChange(userProfile);
    const targetBaselineMinutes = WorkoutTargetConfig[userProfile.longTermGoal] || 0;
    const target = latestWeeklyTargetMinutesChange.targetAboveBaseline + targetBaselineMinutes;
    const dateOfLastTargetChange = new Date(latestWeeklyTargetMinutesChange.dateOfTargetChange).getTime();

    graphDateLabels.forEach((element, index) => {
      const elementDate = new Date(element);
      const endOfDayGraphDataLabel = TimeSpan.createEndOfDayDateInTimezone(elementDate, timezone).getTime();

      if (dateOfLastTargetChange >= endOfDayGraphDataLabel) {
        positionOnGraphOfLastTargetChange = index;
      }
    });

    return {
      positionOnGraphOfLastTargetChange,
      target,
    };
  }
}
